import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Tag, message, Space, Input, Select, Modal, Empty, Form, Popconfirm, Alert, Row, Col, Upload, Image, Spin, Steps, Progress, DatePicker, Tooltip, Tabs, Checkbox, Divider } from 'antd';
import { EyeOutlined, TeamOutlined, PlusOutlined, CopyOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DownloadOutlined, UploadOutlined, FileTextOutlined, InboxOutlined, CheckCircleOutlined, ReloadOutlined, LockOutlined, SearchOutlined, UserOutlined, WarningOutlined, CarOutlined, GiftOutlined } from '@ant-design/icons';
import VoucherRedeemModal from '../components/VoucherRedeemModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/zh-cn';
import authService from '../utils/auth';
import { reportExport } from '../utils/exportLog';
import { getCountryDisplay, getAllCountries } from '../utils/countryCode';
import { validatePassportName, getNameFormatHint } from '../utils/nameValidator';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ChinaDocAction, { getChinaDocLabel } from '../components/ChinaDocAction';
import PassportReuseTag from '../components/PassportReuseTag';
import './SalesTours.css';

moment.locale('zh-cn');

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { Option } = Select;
const { Dragger } = Upload;
const { Step } = Steps;

const normalizePickupPoints = (points) => {
  if (!points) return [];
  return points.map(p => typeof p === 'string' ? { name: p, time: '' } : p);
};

// 从 tour 或其 destination 获取上车点（tour 自身优先）
const getTourPickupPoints = (tourInfo) => {
  if (!tourInfo) return [];
  const raw = tourInfo.pickupPoints || tourInfo.destination?.pickupPoints;
  return normalizePickupPoints(raw);
};

// OCR 日期统一按 DD/MM/YYYY 优先解析成 YYYY-MM-DD（原生 date input 只认这个格式）。
// 不指定格式时 moment 回落到 new Date()，会按美式 MM/DD/YYYY 解析：
//   "19/11/1961" → Invalid date（19 不是月）→ 字段显示空白
//   "03/08/2026" → 2026-03-08（3月8日）→ 日月颠倒且无报错
// 解析失败返回空串留人工补，绝不塞 "Invalid date"（会被存成 0000-00-00，记录从此编辑不了）。
const parseOcrDate = (v) => {
  if (!v) return '';
  const m = moment(v, ['DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', 'DD-MM-YYYY'], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

function SalesTours() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [searchDepartureDate, setSearchDepartureDate] = useState(null);
  const [searchTouristName, setSearchTouristName] = useState('');
  const [searchingTourist, setSearchingTourist] = useState(false);
  const [allTourists, setAllTourists] = useState([]);
  const [touristsModalVisible, setTouristsModalVisible] = useState(false);
  const [selectedTourTourists, setSelectedTourTourists] = useState([]);
  const [selectedTourInfo, setSelectedTourInfo] = useState(null);
  const [touristsLoading, setTouristsLoading] = useState(false);
  const [addTouristModalVisible, setAddTouristModalVisible] = useState(false);
  const [editingTourist, setEditingTourist] = useState(null);
  const [editingRemarks, setEditingRemarks] = useState(null);
  const [remarksModalVisible, setRemarksModalVisible] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newPassportPhoto, setNewPassportPhoto] = useState(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadingTourist, setUploadingTourist] = useState(null);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const [recognizedData, setRecognizedData] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [documentType, setDocumentType] = useState('passport');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmChecks, setConfirmChecks] = useState({ name: false, docNo: false, birthDate: false, expiryDate: false });
  const [ocrNameMismatch, setOcrNameMismatch] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [passportExpiryWarning, setPassportExpiryWarning] = useState(null);
  const [passportValidityWarning, setPassportValidityWarning] = useState(null);
  const [nzPassportWarning, setNzPassportWarning] = useState(null);
  const [passportModalVisible, setPassportModalVisible] = useState(false);
  // 查看弹窗是否连身份证/台胞证一起展示：操作列的眼睛=全部证件，护照列的「查看」=只看护照
  const [viewWithChinaDoc, setViewWithChinaDoc] = useState(false);
  const [passportModalRecord, setPassportModalRecord] = useState(null);
  // Voucher 快捷核销
  const [redeemModalVisible, setRedeemModalVisible] = useState(false);
  const [redeemingTourist, setRedeemingTourist] = useState(null);
  // 当前产品可用的Add-On列表
  const [availableAddOns, setAvailableAddOns] = useState([]);
  // 上传链接弹窗（一日游）
  const [uploadLinkModalVisible, setUploadLinkModalVisible] = useState(false);
  const [newTouristUploadUrl, setNewTouristUploadUrl] = useState('');
  // 一日游上传：证件类型 & 邮箱验证
  const [uploadDocumentType, setUploadDocumentType] = useState('passport');
  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [verifyContext, setVerifyContext] = useState('');
  const [discountAlert, setDiscountAlert] = useState(null);
  const [mainContactIndex, setMainContactIndex] = useState(0);
  const [pendingDocs, setPendingDocs] = useState({}); // { [index]: { file, status: 'idle'|'ocr'|'done'|'error', ocrData, previewUrl } }
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [previewDocVisible, setPreviewDocVisible] = useState(false);
  const [previewDocUrl, setPreviewDocUrl] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [salesActiveTab, setSalesActiveTab] = useState(() => searchParams.get('tab') || sessionStorage.getItem('salesTours_activeTab') || 'standard');
  const [daytourDocUploading, setDaytourDocUploading] = useState(null); // tourist id being uploaded
  const [form] = Form.useForm();
  const [uploadForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [remarksForm] = Form.useForm();
  const userInfo = authService.getUserInfo();

  useEffect(() => {
    fetchTours();
  }, []);

  // URL tab 参数变化时同步
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && (tab === 'standard' || tab === 'inbound' || tab === 'daytour')) {
      setSalesActiveTab(tab);
      sessionStorage.setItem('salesTours_activeTab', tab);
    }
  }, [searchParams]);

  const fetchTours = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tours`, {
        // mine=1：要「我名下的游客数」而不是全团人数。不带这个参数时后端只对
        // role=salesperson 生效，管理员/运营自己带的客人会全部显示成 0
        params: { mine: 1 },
        headers: authService.getAuthHeaders()
      });
      setTours(response.data.data);
    } catch (error) {
      message.error('获取旅游产品列表失败');
      console.error('Fetch tours error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 搜索游客姓名
  const searchTouristsByName = async (name) => {
    if (!name || name.trim() === '') {
      setAllTourists([]);
      return;
    }

    setSearchingTourist(true);
    try {
      const response = await axios.get(`${API_BASE}/tourists`, {
        headers: authService.getAuthHeaders()
      });

      if (response.data.success) {
        // 过滤匹配的游客（仅限当前销售人员的游客）
        const searchLower = name.toLowerCase().trim();
        const matchedTourists = response.data.data.filter(tourist =>
          (tourist.salespersonId === userInfo?.id || tourist.salesName === userInfo?.name) && (
            tourist.touristName?.toLowerCase().includes(searchLower) ||
            tourist.passportName?.toLowerCase().includes(searchLower) ||
            tourist.passportNumber?.toLowerCase().includes(searchLower) ||
            tourist.ekok?.toLowerCase().includes(searchLower)
          )
        );
        setAllTourists(matchedTourists);
      }
    } catch (error) {
      console.error('搜索游客失败:', error);
    } finally {
      setSearchingTourist(false);
    }
  };

  const handleViewTourists = async (tour) => {
    setSelectedTourInfo(tour);
    setTouristsModalVisible(true);
    setTouristsLoading(true);
    setAvailableAddOns([]); // 重置Add-Ons列表

    try {
      // 只拉当前团的游客（避免拉全库 700+ 条 + 给每个生成 S3 预签名 URL 拖慢）
      const response = await axios.get(`${API_BASE}/tourists`, {
        params: { tourId: tour.id },
        headers: authService.getAuthHeaders()
      });

      // 只显示自己的客人：按 salespersonId 关联为主（可靠），salesName 兜底（兼容旧数据）。
      // 同步游客的 salesName 是 TTOUR broker 原文(如"A J Wang")≠ 销售 passport 名("A.J.")，只能靠 id。
      const myTourists = (response.data.data || []).filter(tourist =>
        tourist.salespersonId === userInfo?.id || tourist.salesName === userInfo?.name
      );

      setSelectedTourTourists(myTourists);

      // 一日游：获取目的地详情（含上车点）
      if (tour.tourType === 'daytour' && tour.destinationId) {
        try {
          const destResponse = await axios.get(`${API_BASE}/destinations/${tour.destinationId}`, {
            headers: authService.getAuthHeaders()
          });
          if (destResponse.data.success) {
            setSelectedTourInfo(prev => ({ ...prev, destination: destResponse.data.data }));
          }
        } catch (error) {
          console.error('获取目的地详情失败:', error);
        }
      }

      // 获取该产品可用的Add-On列表
      if (tour.destinationId) {
        try {
          const addOnResponse = await axios.get(`${API_BASE}/tours/${tour.id}/addons`, {
            headers: authService.getAuthHeaders()
          });
          if (addOnResponse.data.success) {
            setAvailableAddOns(addOnResponse.data.data.availableAddOns || []);
          }
        } catch (error) {
          console.error('获取Add-On列表失败:', error);
          setAvailableAddOns([]);
        }
      }
    } catch (error) {
      message.error('获取游客列表失败');
      console.error('Fetch tourists error:', error);
    } finally {
      setTouristsLoading(false);
    }
  };
  
  const handleAddTourist = async (values) => {
    const isDaytour = selectedTourInfo?.tourType === 'daytour';

    if (isDaytour) {
      // 一日游：批量添加模式
      setSubmittingOrder(true);
      try {
        const mainIdx = mainContactIndex;
        const mainPhone = values.tourists?.[mainIdx]?.phone || values.tourists?.[0]?.phone || null;
        // 计算有多少证件会在创建后立即上传
        const docsToUpload = values.tourists.filter((t, i) => t.discountPrice && pendingDocs[i]?.file).length;
        const payload = {
          tourId: selectedTourInfo.id,
          ekok: values.ekok,
          phone: mainPhone,
          pendingUploads: docsToUpload,
          tourists: values.tourists.map(t => ({
            touristName: t.touristName,
            phone: t.phone || null,
            pickupPoint: t.pickupPoint || null,
            discountPrice: t.discountPrice || false
          }))
        };

        const response = await axios.post(`${API_BASE}/tourists/batch`, payload, {
          headers: authService.getAuthHeaders()
        });

        message.success(`已添加 ${values.tourists.length} 位游客`);

        // 自动上传已选择的证件文件
        const createdList = response.data.data || [];
        let uploadCount = 0;
        for (let i = 0; i < createdList.length; i++) {
          const doc = pendingDocs[i];
          if (doc?.file && createdList[i].id) {
            try {
              const formData = new FormData();
              formData.append('document', doc.file);
              formData.append('documentType', 'passport');
              if (doc.ocrData) {
                formData.append('confirmData', JSON.stringify(doc.ocrData));
              }
              await axios.post(`${API_BASE}/tourists/${createdList[i].id}/update-document`, formData, {
                headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
              });
              uploadCount++;
            } catch (uploadErr) {
              console.error('证件上传失败:', uploadErr);
            }
          }
        }
        if (uploadCount > 0) {
          message.success(`已自动上传 ${uploadCount} 份证件`);
        }

        // 如果有优惠价游客但没上传证件，弹出上传链接
        const discountWithoutDoc = createdList.filter((t, i) => t.discountPrice && !pendingDocs[i]?.file);
        if (discountWithoutDoc.length > 0 && discountWithoutDoc[0].uploadUrl) {
          setNewTouristUploadUrl(discountWithoutDoc[0].uploadUrl);
          setUploadLinkModalVisible(true);
        }

        form.resetFields();
        setMainContactIndex(0);
        setPendingDocs({});
        setAddTouristModalVisible(false);
        handleViewTourists(selectedTourInfo);
      } catch (error) {
        message.error(error.response?.data?.error || '添加游客失败');
      } finally {
        setSubmittingOrder(false);
      }
      return;
    }

    // 出境游：单人添加模式
    const nameValidation = validatePassportName(values.touristName);
    if (!nameValidation.valid) {
      message.error(nameValidation.error);
      return;
    }

    try {
      const payload = {
        tourId: selectedTourInfo.id,
        touristName: nameValidation.formatted,
        passportName: nameValidation.formatted,
        salesName: userInfo.name,
        salespersonId: userInfo.id,
        ekok: values.ekok || null,
        contactPhone: values.contactPhone?.trim() || null
      };

      const res = await axios.post(`${API_BASE}/tourists`, payload, {
        headers: authService.getAuthHeaders()
      });

      const reused = res.data?.reusedPassport;
      if (reused) {
        Modal.success({
          title: '已沿用历史护照',
          content: (
            <div style={{ lineHeight: 1.8 }}>
              <div>游客 {nameValidation.formatted} 已添加，护照信息取自历史订单，无需重新上传。</div>
              <div>来源订单：{reused.fromTourName || '-'}{reused.fromDepartureDate ? `（${reused.fromDepartureDate} 出团）` : ''}</div>
              <div>护照号码：{reused.passportNumber}</div>
              <div>有效期至：{reused.passportExpiryDate}</div>
            </div>
          ),
        });
      } else {
        message.success(`游客 ${nameValidation.formatted} 添加成功`);
      }
      form.resetFields();
      setAddTouristModalVisible(false);
      handleViewTourists(selectedTourInfo);
    } catch (error) {
      message.error(error.response?.data?.error || '添加游客失败');
    }
  };

  // 校验 OCR 日期字段格式（DD/MM/YYYY 或 YYYY-MM-DD）
  const validateOcrDate = (_, value) => {
    if (!value) return Promise.resolve();
    const m = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
    return m.isValid() ? Promise.resolve() : Promise.reject(new Error('日期格式应为 DD/MM/YYYY'));
  };

  // 检查护照有效期是否距离出发日期小于6个月
  const checkPassportExpiry = (expiryDateStr) => {
    const departureDate = selectedTourInfo?.departureDate || editingTourist?.departureDate;
    if (!expiryDateStr || !departureDate) {
      setPassportExpiryWarning(null);
      return;
    }

    const expiryMoment = moment(expiryDateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD']);
    const departureMoment = moment(departureDate);

    if (expiryMoment.isValid() && departureMoment.isValid()) {
      const monthsDiff = expiryMoment.diff(departureMoment, 'months', true);
      if (monthsDiff < 6) {
        setPassportExpiryWarning({
          expiryDate: expiryMoment.format('YYYY-MM-DD'),
          departureDate: departureMoment.format('YYYY-MM-DD'),
          monthsDiff: Math.max(0, monthsDiff).toFixed(1)
        });
        return;
      }
    }
    setPassportExpiryWarning(null);
  };

  // 检查护照有效期与年龄是否匹配（成人10年，儿童5年）
  const checkPassportValidity = (birthDateStr, issueDateStr, expiryDateStr) => {
    if (!birthDateStr || !issueDateStr || !expiryDateStr) {
      setPassportValidityWarning(null);
      return;
    }
    const birthMoment = moment(birthDateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD']);
    const issueMoment = moment(issueDateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD']);
    const expiryMoment = moment(expiryDateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD']);
    if (!birthMoment.isValid() || !issueMoment.isValid() || !expiryMoment.isValid()) {
      setPassportValidityWarning(null);
      return;
    }
    const ageAtIssue = issueMoment.diff(birthMoment, 'years');
    // 用月数计算更精确（各国护照有效期可能差几天，如中国护照10年减1天）
    const validityMonths = expiryMoment.diff(issueMoment, 'months', true);
    const isAdult = ageAtIssue >= 16;
    // 成人护照约10年（~118-120月），儿童约5年（~58-60月）
    // 成人：有效期不足9年（108月）告警；儿童：有效期超过6年（72月）告警
    const displayYears = (validityMonths / 12).toFixed(1);
    if (isAdult && validityMonths < 108) {
      setPassportValidityWarning(`签发时年龄 ${ageAtIssue} 岁（成人），护照有效期应为10年，但识别结果约 ${displayYears} 年，请核实OCR识别是否准确`);
    } else if (!isAdult && validityMonths > 72) {
      setPassportValidityWarning(`签发时年龄 ${ageAtIssue} 岁（儿童），护照有效期应为5年，但识别结果约 ${displayYears} 年，请核实OCR识别是否准确`);
    } else {
      setPassportValidityWarning(null);
    }
  };

  // 重新检查护照有效期（从表单读取最新值）
  const recheckPassportValidity = () => {
    const vals = uploadForm.getFieldsValue();
    checkPassportValidity(vals.birthDate, vals.issueDate, vals.expiryDate);
  };

  // 重新检查NZ护照号码（从表单读取最新值）
  const recheckNZPassport = () => {
    const vals = uploadForm.getFieldsValue();
    checkNZPassportNumber(vals.nationality, vals.passportNumber);
  };

  // 检查新西兰护照号码格式（前两位应为英文字母）
  const checkNZPassportNumber = (nationality, passportNumber) => {
    if (!nationality || !passportNumber) {
      setNzPassportWarning(null);
      return;
    }
    if ((nationality === 'NZL' || nationality === 'NZ') && !/^[A-Za-z]{2}/.test(passportNumber)) {
      setNzPassportWarning(`新西兰护照号码前两位应为英文字母，当前识别为「${passportNumber}」，请核实`);
    } else {
      setNzPassportWarning(null);
    }
  };

  // 检查优惠价资格（一日游专用，55岁以上）
  const checkDiscountEligibility = (birthDateStr) => {
    if (!uploadingTourist?.discountPrice || selectedTourInfo?.tourType !== 'daytour') {
      setDiscountAlert(null);
      return;
    }
    const departureDate = selectedTourInfo?.departureDate;
    if (!birthDateStr || !departureDate) {
      setDiscountAlert(null);
      return;
    }
    const birthMoment = moment(birthDateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD']);
    const departureMoment = moment(departureDate);
    if (!birthMoment.isValid() || !departureMoment.isValid()) {
      setDiscountAlert(null);
      return;
    }
    const age = departureMoment.diff(birthMoment, 'years');
    setDiscountAlert({ eligible: age >= 55, age });
  };

  const handleEditTourist = async (tourist) => {
    try {
      // 从后端获取完整的游客信息（包含S3预签名URL）
      const response = await axios.get(`${API_BASE}/tourists/${tourist.id}`, {
        headers: authService.getAuthHeaders()
      });

      const fullTouristData = response.data.data;
      setEditingTourist(fullTouristData);
      setNewPassportPhoto(null); // 重置上传状态

      const isDaytour = selectedTourInfo?.tourType === 'daytour';

      if (isDaytour) {
        editForm.setFieldsValue({
          touristName: fullTouristData.touristName,
          ekok: fullTouristData.ekok,
          phone: fullTouristData.phone,
          discountPrice: fullTouristData.discountPrice || false,
          documentNumber: fullTouristData.documentNumber,
          birthDate: fullTouristData.birthDate ? moment(fullTouristData.birthDate).format('YYYY-MM-DD') : '',
          pickupPoint: fullTouristData.pickupPoint || undefined,
        });
      } else {
        // 检查护照有效期
        checkPassportExpiry(fullTouristData.passportExpiryDate);

        editForm.setFieldsValue({
          touristName: fullTouristData.touristName,
          ekok: fullTouristData.ekok,
          contactPhone: fullTouristData.contactPhone,
          contactEmail: fullTouristData.contactEmail,
          birthPlace: fullTouristData.birthPlace,
          roomType: fullTouristData.roomType,
          passportName: fullTouristData.passportName,
          passportNumber: fullTouristData.passportNumber,
          nationality: fullTouristData.nationality,
          gender: fullTouristData.gender || 'M',
          passportBirthDate: fullTouristData.passportBirthDate ? moment(fullTouristData.passportBirthDate).format('YYYY-MM-DD') : '',
          passportExpiryDate: fullTouristData.passportExpiryDate ? moment(fullTouristData.passportExpiryDate).format('YYYY-MM-DD') : '',
          passportIssueDate: fullTouristData.passportIssueDate ? moment(fullTouristData.passportIssueDate).format('YYYY-MM-DD') : '',
        });
      }
    } catch (error) {
      message.error('获取游客信息失败');
      console.error('Error fetching tourist:', error);
    }
  };

  const handleEditRemarks = (tourist) => {
    setEditingRemarks(tourist);
    setRemarksModalVisible(true);
    remarksForm.setFieldsValue({
      remarks: tourist.remarks || ''
    });
  };

  const handleUpdateRemarks = async (values) => {
    try {
      await axios.put(`${API_BASE}/tourists/${editingRemarks.id}`, {
        remarks: values.remarks
      }, {
        headers: authService.getAuthHeaders()
      });
      
      message.success('备注更新成功');
      setRemarksModalVisible(false);
      setEditingRemarks(null);
      remarksForm.resetFields();
      
      // 刷新游客列表
      handleViewTourists(selectedTourInfo);
    } catch (error) {
      message.error(error.response?.data?.error || '备注更新失败');
    }
  };

  const handleUpdateTourist = async (values) => {
    const nameValidation = validatePassportName(values.touristName);
    if (!nameValidation.valid) {
      message.error(nameValidation.error);
      return;
    }

    try {
      const isDaytour = selectedTourInfo?.tourType === 'daytour';
      let updateData;

      if (isDaytour) {
        // 如果新勾选了优惠价，订单状态改为待确认
        const newDiscount = values.discountPrice || false;
        const wasDiscount = editingTourist.discountPrice || false;
        updateData = {
          touristName: nameValidation.formatted,
          ekok: values.ekok || null,
          phone: values.phone || null,
          discountPrice: newDiscount,
          documentNumber: values.documentNumber || null,
          tourId: selectedTourInfo.id,
          // 上车点下拉框只在团配了可选上车点时渲染；没渲染时表单里没有这个值，
          // 照样写 null 会把会员系统同步来的上车点清空（2026-10-21 教堂湾 10-24 团两人即此）
          ...(getTourPickupPoints(selectedTourInfo).length > 0 ? { pickupPoint: values.pickupPoint || null } : {}),
          ...(newDiscount && !wasDiscount ? { orderStatus: 'pending' } : {}),
        };
        if (values.birthDate && values.birthDate !== 'Invalid date') {
          updateData.birthDate = values.birthDate;
        }
      } else {
        updateData = {
          touristName: nameValidation.formatted,
          passportName: nameValidation.formatted,
          passportNumber: values.passportNumber || null,
          nationality: values.nationality || null,
          gender: values.gender || null,
          roomType: values.roomType || null,
          ekok: values.ekok || null,
          contactPhone: values.contactPhone || null,
          contactEmail: values.contactEmail || null,
          tourId: selectedTourInfo.id
        };
        // 出生地只在表单确有值时才提交。
        // 原写法 `birthPlace: values.birthPlace || null` 会在表单为空时下发 null，
        // 把 OCR 已识别存好的出生地覆盖掉（线上实测 28 条因此丢失，
        // recognizedData 里还留着 SHANGHAI / JIANGSU 等原值）。
        // 与相邻的 passportBirthDate 等字段保持同一写法：有值才带上。
        if (values.birthPlace && String(values.birthPlace).trim()) {
          updateData.birthPlace = String(values.birthPlace).trim();
        }
        if (values.passportBirthDate && values.passportBirthDate !== 'Invalid date') {
          updateData.passportBirthDate = values.passportBirthDate;
        }
        if (values.passportExpiryDate && values.passportExpiryDate !== 'Invalid date') {
          updateData.passportExpiryDate = values.passportExpiryDate;
        }
        if (values.passportIssueDate && values.passportIssueDate !== 'Invalid date') {
          updateData.passportIssueDate = values.passportIssueDate;
        }
      }

      await axios.put(`${API_BASE}/tourists/${editingTourist.id}`, updateData, {
        headers: authService.getAuthHeaders()
      });
      
      message.success('游客信息更新成功');
      setEditingTourist(null);
      editForm.resetFields();
      // 刷新游客列表
      handleViewTourists(selectedTourInfo);
    } catch (error) {
      message.error(error.response?.data?.error || '更新失败');
    }
  };

  const handleDeleteTourist = async (touristId) => {
    try {
      await axios.delete(`${API_BASE}/tourists/${touristId}`, {
        headers: authService.getAuthHeaders()
      });
      message.success('游客删除成功');
      // 刷新游客列表
      handleViewTourists(selectedTourInfo);
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 更新游客房型
  const handleUpdateRoomType = async (touristId, roomType) => {
    try {
      await axios.put(`${API_BASE}/tourists/${touristId}`, {
        roomType: roomType || null
      }, {
        headers: authService.getAuthHeaders()
      });
      message.success('房型更新成功');
      // 刷新游客列表
      handleViewTourists(selectedTourInfo);
    } catch (error) {
      message.error('房型更新失败');
    }
  };

  // 更新单个Add-On
  const handleUpdateSingleAddOn = async (touristId, addOnKey, value) => {
    try {
      // 先获取当前游客的dayTrips数据
      const tourist = selectedTourTourists.find(t => t.id === touristId);
      const currentDayTrips = tourist?.dayTrips || {};

      // 更新指定的Add-On
      const updatedDayTrips = {
        ...currentDayTrips,
        [addOnKey]: value
      };

      await axios.put(`${API_BASE}/tourists/${touristId}/day-trips`, {
        dayTrips: updatedDayTrips
      }, {
        headers: authService.getAuthHeaders()
      });

      message.success('Add-On更新成功');
      // 刷新游客列表
      handleViewTourists(selectedTourInfo);
    } catch (error) {
      message.error('Add-On更新失败');
    }
  };

  // 打开上传护照/证件弹窗
  const handleUploadPassport = (tourist) => {
    setUploadingTourist(tourist);
    setRecognizedData(null);
    setCurrentStep(0);
    setTempImageUrl(null);
    setTempImageFile(null);
    setUploadProgress(0);
    setProgressText('');
    setDocumentType('passport');
    setUploadDocumentType('passport');
    setDiscountAlert(null);
    setEmailVerified(false);
    setEmailCountdown(0);
    setVerifyContext('');
    uploadForm.resetFields();
    setUploadModalVisible(true);
  };

  // 一日游内联上传证件：直接保存，不做OCR识别
  const handleDaytourInlineUpload = async (file, touristId) => {
    setDaytourDocUploading(touristId);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', 'passport');

    try {
      const saveRes = await axios.post(
        `${API_BASE}/tourists/${touristId}/update-document`,
        formData,
        { headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      if (saveRes.data.success) {
        message.success(saveRes.data.recognizedName
          ? `证件已上传，姓名已识别：${saveRes.data.recognizedName}`
          : '证件已上传');
      } else {
        message.warning(saveRes.data.message || '证件上传失败');
      }
      handleViewTourists(selectedTourInfo);
    } catch (error) {
      message.error('证件上传失败：' + (error.response?.data?.error || error.message));
    } finally {
      setDaytourDocUploading(null);
    }
    return false;
  };

  // 护照/证件图片文件校验：大小 + 分辨率
  const validatePassportFile = (file) => new Promise((resolve) => {
    if (file.size > 5 * 1024 * 1024) {
      message.error('文件超过 5MB，请压缩后再上传');
      return resolve(false);
    }
    const url = URL.createObjectURL(file);
    const img = document.createElement('img');
    img.onload = () => {
      URL.revokeObjectURL(url);
      const minDim = Math.min(img.width, img.height);
      if (minDim < 600) {
        Modal.confirm({
          title: '图片分辨率偏低',
          content: `当前图片尺寸 ${img.width}×${img.height}，分辨率不足可能导致识别不准确，建议上传更清晰的照片。是否继续上传？`,
          okText: '继续上传',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
        return;
      }
      resolve(true);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(true); };
    img.src = url;
  });

  // 处理护照/证件图片上传（预览模式）
  const handleManualPassportUpload = async (file) => {
    if (!await validatePassportFile(file)) return false;
    const isDaytour = selectedTourInfo?.tourType === 'daytour';

    // 一日游：直接保存，不做OCR识别
    if (isDaytour) {
      setUploadingPassport(true);
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', uploadDocumentType);

      try {
        const response = await axios.post(
          `${API_BASE}/tourists/${uploadingTourist.id}/update-document`,
          formData,
          { headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
        );
        if (response.data.success) {
          message.success(response.data.recognizedName
            ? `证件已上传，姓名已识别：${response.data.recognizedName}`
            : '证件已上传');
          setUploadModalVisible(false);
          setUploadingTourist(null);
          setCurrentStep(0);
          setTempImageUrl(null);
          setTempImageFile(null);
          uploadForm.resetFields();
          handleViewTourists(selectedTourInfo);
        } else {
          message.error(response.data.error || '证件上传失败');
        }
      } catch (error) {
        message.error(error.response?.data?.error || '证件上传失败，请重试');
      } finally {
        setUploadingPassport(false);
      }
      return false;
    }

    // 标准产品：OCR预览流程
    const reader = new FileReader();
    reader.onload = (e) => setTempImageUrl(e.target.result);
    reader.readAsDataURL(file);
    setTempImageFile(file);

    const formData = new FormData();
    formData.append('passport', file);
    formData.append('preview', 'true');

    setUploadingPassport(true);
    setUploadProgress(0);

    const progressSteps = [
      { percent: 20, text: '正在上传图片...' },
      { percent: 40, text: '图片质量检查中...' },
      { percent: 60, text: 'AI智能识别中...' },
      { percent: 80, text: '解析护照信息...' },
      { percent: 95, text: '完成识别处理...' }
    ];

    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        setUploadProgress(progressSteps[stepIndex].percent);
        setProgressText(progressSteps[stepIndex].text);
        stepIndex++;
      } else {
        clearInterval(progressInterval);
      }
    }, 800);

    try {
      const response = await axios.post(
        `${API_BASE}/tourists/${uploadingTourist.id}/update-passport-preview`,
        formData,
        { headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );

      // OCR 被禁用：照片已保存，但不识别，直接进入手动编辑游客信息
      if (response.data.ocrDisabled) {
        clearInterval(progressInterval);
        setUploadingPassport(false);
        setUploadProgress(0);
        setProgressText('');
        Modal.warning({
          title: '需要手动录入护照信息',
          content: response.data.message || '由于您多次没有人工核对护照信息，您被禁用护照识别功能，请手动录入护照信息',
          okText: '我知道了',
          onOk: () => {
            setUploadModalVisible(false);
            setCurrentStep(0);
            setTempImageUrl(null);
            setTempImageFile(null);
            uploadForm.resetFields();
            // 刷新当前团游客列表，让 passportPhoto 拿到最新值，然后进编辑弹窗
            handleViewTourists(selectedTourInfo).finally(() => {
              const refreshed = { ...uploadingTourist, passportPhoto: response.data.data?.passportPhoto || uploadingTourist.passportPhoto };
              handleEditTourist(refreshed);
            });
          }
        });
        return false;
      }

      if (response.data.success) {
        clearInterval(progressInterval);
        setUploadProgress(100);
        setProgressText('识别完成！');
        setRecognizedData(response.data.data);

        let nationality = response.data.data.nationality || 'CHN';
        if (nationality === 'CHI') nationality = 'CHN';

        const formValues = {
          fullName: response.data.data.recognizedName || response.data.data.fullName || '',
          passportNumber: response.data.data.passportNumber || '',
          gender: response.data.data.gender || 'M',
          issueDate: response.data.data.issueDate || '',
          expiryDate: response.data.data.expiryDate || '',
          birthDate: response.data.data.birthDate || '',
          birthPlace: response.data.data.birthPlace || '',
          nationality: nationality
        };

        uploadForm.setFieldsValue(formValues);
        // 检查证件姓名与录入姓名是否一致
        const ocrName = (formValues.fullName || '').toUpperCase().replace(/\s+/g, '');
        const originalName = (uploadingTourist?.touristName || '').toUpperCase().replace(/\s+/g, '');
        if (ocrName && originalName && ocrName !== originalName) {
          setOcrNameMismatch(`护照识别姓名：${formValues.fullName}`);
          // 保留原始录入姓名
          uploadForm.setFieldsValue({ fullName: uploadingTourist.touristName });
        } else {
          setOcrNameMismatch('');
        }
        // 检查护照有效期与年龄匹配
        checkPassportValidity(formValues.birthDate, formValues.issueDate, formValues.expiryDate);
        // 检查护照有效期距出团日期是否不足6个月
        checkPassportExpiry(formValues.expiryDate);
        // 检查新西兰护照号码格式
        checkNZPassportNumber(formValues.nationality, formValues.passportNumber);
        setCurrentStep(1);
        message.success('护照识别成功，请确认信息！');
      }
    } catch (error) {
      clearInterval(progressInterval);
      const respData = error.response?.data;
      if (respData?.error === 'INCOMPLETE_PASSPORT' || respData?.error === 'MRZ_UNVERIFIED') {
        message.error(respData.message || '护照照片未拍全，请把护照信息页所有信息和底部两行机读码（MRZ）完整拍入、无裁切遮挡后重新上传', 8);
      } else {
        message.error(respData?.error || '识别失败，请重试');
      }
      setCurrentStep(0);
      setTempImageUrl(null);
      setTempImageFile(null);
      setUploadProgress(0);
      setProgressText('');
    } finally {
      setUploadingPassport(false);
    }
    return false;
  };

  // 倒计时效果
  useEffect(() => {
    let timer;
    if (confirmModalVisible && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [confirmModalVisible, countdown]);


  // 显示确认弹窗
  const handleFinalSubmit = async () => {
    try {
      const values = await uploadForm.validateFields();
      const isDaytour = selectedTourInfo?.tourType === 'daytour';
      if (isDaytour && values.contactEmail && !emailVerified) {
        message.error('请先验证邮箱');
        return;
      }
      setCountdown(5);
      setConfirmChecks({ name: false, docNo: false, birthDate: false, expiryDate: false });
      setConfirmModalVisible(true);
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写所有必填项');
      }
    }
  };

  // 实际提交护照/证件信息
  const handleConfirmAndSubmit = async () => {
    try {
      const values = uploadForm.getFieldsValue();
      const nameValidation = validatePassportName(values.fullName);
      if (!nameValidation.valid) {
        message.error(nameValidation.error);
        return;
      }

      setConfirming(true);
      setConfirmModalVisible(false);

      const isDaytour = selectedTourInfo?.tourType === 'daytour';
      const formData = new FormData();

      if (isDaytour) {
        formData.append('document', tempImageFile);
        formData.append('documentType', uploadDocumentType);
        formData.append('confirmData', JSON.stringify({
          fullName: nameValidation.formatted,
          documentNumber: values.documentNumber,
          gender: values.gender,
          issueDate: values.issueDate,
          expiryDate: values.expiryDate,
          birthDate: values.birthDate,
          birthPlace: values.birthPlace,
          nationality: values.nationality,
          phone: values.phone,
          contactEmail: values.contactEmail
        }));
      } else {
        formData.append('passport', tempImageFile);
        formData.append('confirmData', JSON.stringify({
          fullName: nameValidation.formatted,
          passportNumber: values.passportNumber,
          gender: values.gender,
          issueDate: values.issueDate,
          expiryDate: values.expiryDate,
          birthDate: values.birthDate,
          birthPlace: values.birthPlace,
          nationality: values.nationality
        }));
      }

      const submitEndpoint = isDaytour
        ? `${API_BASE}/tourists/${uploadingTourist.id}/update-document`
        : `${API_BASE}/tourists/${uploadingTourist.id}/update-passport`;

      const response = await axios.post(submitEndpoint, formData, {
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        message.success(isDaytour ? '证件信息已确认并保存！' : '护照信息已确认并保存！');
        setUploadModalVisible(false);
        setUploadingTourist(null);
        setCurrentStep(0);
        setTempImageUrl(null);
        setTempImageFile(null);
        uploadForm.resetFields();
        handleViewTourists(selectedTourInfo);
      }
    } catch (error) {
      if (error.response) message.error(error.response.data.error || '提交失败，请重试');
      else if (error.errorFields) message.error('请填写所有必填项');
      else message.error('提交失败，请重试');
    } finally {
      setConfirming(false);
    }
  };

  // 重新上传
  const handleResetUpload = () => {
    setCurrentStep(0);
    setTempImageUrl(null);
    setTempImageFile(null);
    setRecognizedData(null);
    setUploadProgress(0);
    setProgressText('');
    setDocumentType('passport');
    setEmailVerified(false);
    setEmailCountdown(0);
    setVerifyContext('');
    uploadForm.resetFields();
  };

  // 邮箱验证相关（一日游）
  const generateVerifyContext = () => 'ctx-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();

  const handleSendVerificationCode = async () => {
    const email = uploadForm.getFieldValue('contactEmail');
    if (!email) { message.error('请先输入邮箱地址'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { message.error('邮箱格式不正确'); return; }
    let ctx = verifyContext;
    if (!ctx) {
      ctx = uploadingTourist?.uploadLink || generateVerifyContext();
      setVerifyContext(ctx);
    }
    setSendingCode(true);
    try {
      const response = await axios.post(`${API_BASE}/email-verification/send-code`, {
        email, context: ctx
      }, { headers: authService.getAuthHeaders() });
      if (response.data.success) {
        message.success('验证码已发送，请查收邮件');
        setEmailCountdown(60);
        const timer = setInterval(() => {
          setEmailCountdown(prev => { if (prev <= 1) { clearInterval(timer); return 0; } return prev - 1; });
        }, 1000);
      }
    } catch (error) {
      message.error(error.response?.data?.error || '发送验证码失败');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    const email = uploadForm.getFieldValue('contactEmail');
    const code = uploadForm.getFieldValue('verificationCode');
    if (!email || !code) { message.error('请输入邮箱和验证码'); return; }
    setVerifyingCode(true);
    try {
      const response = await axios.post(`${API_BASE}/email-verification/verify-code`, {
        email, context: verifyContext, code
      }, { headers: authService.getAuthHeaders() });
      if (response.data.success) {
        setEmailVerified(true);
        message.success('邮箱验证成功');
      }
    } catch (error) {
      message.error(error.response?.data?.error || '验证码验证失败');
    } finally {
      setVerifyingCode(false);
    }
  };

  const copyUploadLink = async (uploadLink) => {
    const fullUrl = `${window.location.origin}/upload/${uploadLink}`;
    
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(fullUrl);
        message.success('链接已复制');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        
        try {
          document.execCommand('copy');
          message.success('链接已复制');
        } catch (err) {
          message.error('复制失败，请手动复制');
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      message.error('复制失败');
    }
  };

  // 导出护照照片打包下载
  const handleExportPhotos = async () => {
    try {
      const touristsWithPhotos = selectedTourTourists.filter(tourist => tourist.passportPhoto);
      
      if (touristsWithPhotos.length === 0) {
        message.warning('暂无护照照片可导出');
        return;
      }

      message.loading('正在打包护照照片...', 2);
      
      const response = await axios.post(`${API_BASE}/export/passport-photos`, {
        tourId: selectedTourInfo.id,
        touristIds: touristsWithPhotos.map(t => t.id)
      }, {
        headers: authService.getAuthHeaders(),
        responseType: 'blob'
      });

      // 创建下载链接
      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedTourInfo.productName}_护照照片_${moment().format('YYYYMMDD')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      message.success('护照照片导出成功');
    } catch (error) {
      if (error.response?.status === 404) {
        // 如果后端还没有导出接口，使用前端方式
        handleClientSideExport();
      } else {
        message.error('导出失败：' + (error.response?.data?.error || error.message));
      }
    }
  };

  // 客户端方式导出（备用方案）
  const handleClientSideExport = async () => {
    const touristsWithPhotos = selectedTourTourists.filter(tourist => tourist.passportPhoto);
    
    if (touristsWithPhotos.length === 0) {
      message.warning('暂无护照照片可导出');
      return;
    }

    try {
      message.info(`开始下载 ${touristsWithPhotos.length} 张护照照片`);
      
      for (let i = 0; i < touristsWithPhotos.length; i++) {
        const tourist = touristsWithPhotos[i];
        const photoUrl = `${API_BASE.replace('/api', '')}${tourist.passportPhoto}`;
        
        // 创建下载链接
        const link = document.createElement('a');
        link.href = photoUrl;
        link.download = `${tourist.touristName}_护照.jpg`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // 添加延迟避免浏览器阻止多文件下载
        if (i < touristsWithPhotos.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      reportExport({ tourId: selectedTourInfo.id, kind: 'photo_direct', tourists: touristsWithPhotos, page: 'sales-tours' });
      message.success('护照照片下载完成');
    } catch (error) {
      message.error('下载失败');
    }
  };

  // CSV导出功能
  // 一日游：导出导游名单 PDF
  const handleExportGuidePDF = async () => {
    const exportTourists = selectedTourTourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded');
    if (exportTourists.length === 0) {
      message.warning('暂无游客记录可导出');
      return;
    }

    const sorted = [...exportTourists].sort((a, b) => {
      const pickupA = a.pickupPoint || '';
      const pickupB = b.pickupPoint || '';
      if (pickupA !== pickupB) return pickupA.localeCompare(pickupB, 'zh');
      const salesA = a.salesName || '';
      const salesB = b.salesName || '';
      if (salesA !== salesB) return salesA.localeCompare(salesB, 'zh');
      return (a.touristName || '').localeCompare(b.touristName || '', 'zh');
    });

    const pickupCounts = {};
    sorted.forEach(t => {
      const key = t.pickupPoint || '未选择';
      pickupCounts[key] = (pickupCounts[key] || 0) + 1;
    });

    const depDate = selectedTourInfo.departureDate ? moment(selectedTourInfo.departureDate).format('YYYY-MM-DD') : '';
    const pickupSummary = Object.entries(pickupCounts).map(([p, c]) => `${p}: ${c}人`).join('　');

    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;background:#fff;padding:30px;font-family:"Microsoft YaHei","PingFang SC",sans-serif;';
    container.innerHTML = `
      <h2 style="text-align:center;margin:0 0 4px;font-size:18px;">${selectedTourInfo.productName}</h2>
      <p style="text-align:center;margin:0 0 12px;font-size:13px;color:#666;">${depDate}　　总人数: ${sorted.length}</p>
      <p style="text-align:center;margin:0 0 16px;font-size:12px;color:#888;">${pickupSummary}</p>
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <thead>
          <tr style="background:#1890ff;color:#fff;">
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:center;width:40px;">序号</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:left;">客人姓名</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:left;">销售</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:left;">上车点</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:left;">联系电话</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:center;">票种</th>
          </tr>
        </thead>
        <tbody>
          ${sorted.map((t, i) => `
            <tr style="background:${i % 2 === 0 ? '#fff' : '#fafafa'};">
              <td style="padding:6px;border:1px solid #e8e8e8;text-align:center;">${i + 1}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;">${t.touristName || '-'}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;">${t.salesName || '-'}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;">${t.pickupPoint || '-'}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;">${t.phone || t.contactPhone || '-'}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;text-align:center;">${t.discountPrice ? '优惠' : '正价'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, { scale: 1.5, useCORS: true });
      document.body.removeChild(container);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgRatio = pdfWidth / canvas.width;
      const scaledHeight = canvas.height * imgRatio;
      const pageHeight = pdfHeight - 10;
      const totalPages = Math.ceil(scaledHeight / pageHeight);
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        const sourceY = (page * pageHeight) / imgRatio;
        const sourceH = Math.min(pageHeight / imgRatio, canvas.height - sourceY);
        const destH = sourceH * imgRatio;
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.ceil(sourceH);
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceH, 0, 0, canvas.width, sourceH);
        pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.75), 'JPEG', 0, 5, pdfWidth, destH);
      }
      pdf.save(`${selectedTourInfo.productName}_${depDate.replace(/-/g, '')}.pdf`);
      reportExport({ tourId: selectedTourInfo.id, kind: 'guide_pdf', tourists: sorted, page: 'sales-tours' });
      message.success(`已导出 ${sorted.length} 条记录`);
    } catch (err) {
      document.body.removeChild(container);
      console.error('导出PDF失败:', err);
      message.error('导出PDF失败');
    }
  };

  // 出境游：导出CSV
  const handleExportCSV = () => {
    const exportTourists = selectedTourTourists.filter(tourist => tourist.uploadStatus === 'verified');
    if (exportTourists.length === 0) {
      message.warning('暂无已验证的游客记录可导出');
      return;
    }

    const depDate = selectedTourInfo.departureDate ? moment(selectedTourInfo.departureDate).format('YYYYMMDD') : '';
    const fileName = `${selectedTourInfo.productName}_${depDate}.csv`;

    // 第一列固定护照姓名（与管理端出票导出口径一致：名字以护照为准，TTOUR 订单名只作对账）
    const csvHeaders = ['护照姓名', 'TTOUR订单名', '护照号码', '国籍', '性别', '出生日期', '护照有效期', '签发日期', '添加时间'];
    const csvContent = [
      csvHeaders.join(','),
      ...exportTourists.map(tourist => {
        return [
          tourist.passportName || tourist.touristName || '',
          tourist.touristName || '',
          tourist.passportNumber || '',
          tourist.nationality || '',
          tourist.gender === 'M' ? '男' : (tourist.gender === 'F' ? '女' : ''),
          tourist.passportBirthDate ? moment(tourist.passportBirthDate).format('YYYY-MM-DD') : '',
          tourist.passportExpiryDate ? moment(tourist.passportExpiryDate).format('YYYY-MM-DD') : '',
          tourist.passportIssueDate ? moment(tourist.passportIssueDate).format('YYYY-MM-DD') : '',
          tourist.createdAt ? moment(tourist.createdAt).format('YYYY-MM-DD HH:mm') : ''
        ].map(f => `"${f}"`).join(',');
      })
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    reportExport({ tourId: selectedTourInfo.id, kind: 'tour_csv', tourists: exportTourists, page: 'sales-tours' });
    message.success(`已导出 ${exportTourists.length} 条游客记录`);
  };

  // 护照照片上传处理
  const handlePassportUpload = async (file) => {
    if (!await validatePassportFile(file)) return false;
    setUploadingPhoto(true);
    
    const formData = new FormData();
    formData.append('passport', file);
    formData.append('touristId', editingTourist.id);

    try {
      const response = await axios.post(
        `${API_BASE}/tourists/${editingTourist.id}/update-passport`,
        formData,
        {
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        // 更新本地状态
        setNewPassportPhoto(response.data.passportPhoto);
        message.success('护照照片上传成功');
        
        // 如果有识别结果，更新表单
        if (response.data.recognizedData) {
          const data = response.data.recognizedData;
          editForm.setFieldsValue({
            passportName: data.fullName || data.recognizedName,
            passportNumber: data.passportNumber,
            nationality: data.nationality,
            gender: data.gender,
            birthPlace: data.birthPlace,
            // ⚠ OCR 返回 DD/MM/YYYY，必须显式给格式（详见 parseOcrDate 注释）
            passportBirthDate: parseOcrDate(data.birthDate),
            passportExpiryDate: parseOcrDate(data.expiryDate),
            passportIssueDate: parseOcrDate(data.issueDate)
          });

          // 检查护照有效期是否距离出发日期小于6个月
          checkPassportExpiry(data.expiryDate);

          message.info('护照信息已自动识别并填入表单，请确认后保存');
        }
      }
    } catch (error) {
      {
        const respData = error.response?.data;
        if (respData?.error === 'INCOMPLETE_PASSPORT' || respData?.error === 'MRZ_UNVERIFIED') {
          message.error(respData.message || '护照照片未拍全，请把护照信息页所有信息和底部两行机读码（MRZ）完整拍入、无裁切遮挡后重新上传', 8);
        } else {
          message.error('护照照片上传失败：' + (respData?.error || error.message));
        }
      }
    } finally {
      setUploadingPhoto(false);
    }

    return false; // 阻止默认上传行为
  };

  const getColumns = (isDaytourTab) => [
    {
      title: '团号',
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      ellipsis: true,
      render: (name, record) => (
        <span>
          {name}
          {record.touristInfoLocked && (
            <Tooltip title="游客信息已锁定">
              <LockOutlined style={{ color: '#ff4d4f', marginLeft: 6 }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: '产品',
      dataIndex: ['destination', 'name'],
      key: 'destination',
      width: 120,
      ellipsis: true,
      render: (name) => name ? <Tag color="purple">{name}</Tag> : <span style={{ color: '#999' }}>未设置</span>,
    },
    {
      title: '出团日期',
      dataIndex: 'departureDate',
      key: 'departureDate',
      width: 120,
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: '我的游客数',
      key: 'touristCount',
      width: 120,
      render: (_, record) => {
        const total = record.touristCount || 0;
        const verified = record.verifiedCount || 0;
        const pending = record.pendingCount || 0;
        const isDaytour = record.tourType === 'daytour';

        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>总数: {total}</div>
            {total > 0 && (
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                <span style={{ color: '#52c41a' }}>{isDaytour ? '已确认' : '已验证'}: {verified}</span>
                {pending > 0 && (
                  <>
                    {' • '}
                    <span style={{ color: '#faad14' }}>{isDaytour ? '待确认' : '待处理'}: {pending}</span>
                  </>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '剩余座位',
      key: 'remainingSeats',
      width: 130,
      render: (_, record) => {
        if (record.tourType !== 'daytour') return '-';
        if (record.totalSeats == null) return '-';
        const capacity = record.totalSeats;
        const booked = record.bookedSeats || 0;
        const pct = capacity > 0 ? Math.min(Math.round((booked / capacity) * 100), 100) : 0;
        const isFull = booked >= capacity && capacity > 0;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: isFull ? '#ff4d4f' : '#1890ff', borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 500, color: isFull ? '#ff4d4f' : '#666', whiteSpace: 'nowrap' }}>
                {booked}/{capacity}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            size="small"
            type="primary"
            icon={<TeamOutlined />}
            onClick={() => handleViewTourists(record)}
          >
            管理游客
          </Button>
        </Space>
      ),
    },
  ];

  // 筛选旅游产品
  const filteredTours = tours.filter(tour => {
    // 过滤已过期团期（出发日期在今天之前）
    if (!showExpired) {
      const today = moment().startOf('day');
      if (moment(tour.departureDate).isBefore(today)) return false;
    }

    // 按产品名称过滤
    const searchFilter = searchText === '' ||
      tour.destination?.name?.toLowerCase().includes(searchText.toLowerCase());

    // 按出团日期过滤
    const dateFilter = !searchDepartureDate ||
      moment(tour.departureDate).format('YYYY-MM-DD') === searchDepartureDate;

    // 按游客姓名过滤（如果有搜索结果，只显示包含匹配游客的产品）
    const touristFilter = searchTouristName === '' || allTourists.length === 0 ||
      allTourists.some(t => t.tourId === tour.id);

    return searchFilter && dateFilter && touristFilter;
  });

  const sortedTours = [...filteredTours].sort((a, b) => {
    const da = new Date(a.departureDate);
    const db = new Date(b.departureDate);
    return showExpired ? db - da : da - db;
  });
  // 按 tab 匹配团类型：出境游=standard(含未知)、入境游=inbound、一日游=daytour
  const matchesTab = (t, tab) => tab === 'daytour' ? t.tourType === 'daytour'
    : tab === 'inbound' ? t.tourType === 'inbound'
    : (t.tourType !== 'daytour' && t.tourType !== 'inbound');
  const standardTours = sortedTours.filter(t => matchesTab(t, 'standard'));
  const inboundTours = sortedTours.filter(t => matchesTab(t, 'inbound'));
  const daytourTours = sortedTours.filter(t => t.tourType === 'daytour');
  const currentTours = salesActiveTab === 'daytour' ? daytourTours
    : salesActiveTab === 'inbound' ? inboundTours : standardTours;

  return (
    <div className="sales-tours">
      <Card 
        title={
          <div className="page-header">
            <h3>出团管理</h3>
          </div>
        }
        className="tours-card"
      >
        <div className="info-banner">
          <div className="banner-content">
            <h4>👋 欢迎，{userInfo?.name}！</h4>
            <p>选择一个旅游产品开始添加和管理您的游客。您可以为每个产品创建游客档案并生成护照上传链接。</p>
          </div>
        </div>

        <div className="filter-bar">
          <Row gutter={16} align="middle">
            <Col>
              <Input.Search
                placeholder="搜索产品名称"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 200 }}
                allowClear
              />
            </Col>
            <Col>
              <Select
                placeholder="选择出团日期"
                value={searchDepartureDate || undefined}
                onChange={(val) => setSearchDepartureDate(val || null)}
                style={{ width: 160 }}
                allowClear
              >
                {[...new Set(tours.filter(t => {
                  if (!matchesTab(t, salesActiveTab)) return false;
                  if (!showExpired) {
                    return !moment(t.departureDate).isBefore(moment().startOf('day'));
                  }
                  return true;
                }).map(t => moment(t.departureDate).format('YYYY-MM-DD')))].sort().map(d => (
                  <Option key={d} value={d}>{d}</Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Input.Search
                placeholder="搜索游客姓名/护照号/EKOK"
                value={searchTouristName}
                onChange={(e) => {
                  setSearchTouristName(e.target.value);
                  if (!e.target.value) {
                    setAllTourists([]);
                  }
                }}
                onSearch={(value) => searchTouristsByName(value)}
                style={{ width: 220 }}
                allowClear
                enterButton={<SearchOutlined />}
                loading={searchingTourist}
                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
              />
            </Col>
            <Col>
              <Checkbox checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)}>往期开团</Checkbox>
            </Col>
            {allTourists.length > 0 && (
              <Col>
                <Tag color="blue">
                  找到 {allTourists.length} 位游客
                </Tag>
              </Col>
            )}
          </Row>
        </div>

        <Table
          columns={getColumns(salesActiveTab === 'daytour')}
          dataSource={currentTours}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          size="small"
          onRow={() => ({ style: { height: '48px' } })}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个产品`,
          }}
        />
      </Card>

      {/* 游客列表弹窗 - 表格形式 */}
      <Modal
        title={
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '8px 0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                fontSize: 18,
                fontWeight: 600,
                color: '#1890ff'
              }}>
                {selectedTourInfo?.productName}
              </span>
              <Tag color="blue" style={{ fontSize: 13, padding: '2px 8px' }}>
                共 {selectedTourTourists.length} 人
              </Tag>
              <Tag color="green" style={{ fontSize: 13, padding: '2px 8px' }}>
                {moment(selectedTourInfo?.departureDate).format('YYYY-MM-DD')} 出发
              </Tag>
              {selectedTourInfo?.tourType === 'daytour' && (
                <Tag color="orange" style={{ fontSize: 13, padding: '2px 8px' }}>一日游</Tag>
              )}
              {selectedTourInfo?.tourType === 'daytour' && selectedTourInfo?.totalSeats != null && (
                <Tag
                  color={
                    (selectedTourInfo.remainingSeats ?? (selectedTourInfo.totalSeats - (selectedTourInfo.bookedSeats || 0))) <= 5
                      ? 'red'
                      : 'cyan'
                  }
                  style={{ fontSize: 13, padding: '2px 8px' }}
                >
                  剩余座位: {selectedTourInfo.remainingSeats ?? (selectedTourInfo.totalSeats - (selectedTourInfo.bookedSeats || 0))}/{selectedTourInfo.totalSeats}
                </Tag>
              )}
              {selectedTourInfo?.touristInfoLocked && (
                <Tag color="red" icon={<LockOutlined />} style={{ fontSize: 13, padding: '2px 8px' }}>
                  已锁定
                </Tag>
              )}
            </div>
            {selectedTourTourists.length > 0 && (
              <div style={{
                fontSize: 13,
                color: '#595959',
                display: 'flex',
                gap: '16px'
              }}>
                {selectedTourInfo?.tourType === 'daytour' ? (
                  <>
                    <span>
                      已确认: <strong style={{ color: '#52c41a' }}>{selectedTourTourists.filter(t => t.orderStatus === 'confirmed').length}</strong>
                    </span>
                    <span>
                      待确认: <strong style={{ color: '#faad14' }}>{selectedTourTourists.filter(t => t.orderStatus === 'pending').length}</strong>
                    </span>
                    {selectedTourTourists.filter(t => t.orderStatus === 'cancelled').length > 0 && (
                      <span>
                        已取消: <strong style={{ color: '#ff4d4f' }}>{selectedTourTourists.filter(t => t.orderStatus === 'cancelled').length}</strong>
                      </span>
                    )}
                    {selectedTourTourists.filter(t => t.orderStatus === 'refunded').length > 0 && (
                      <span>
                        退款: <strong style={{ color: '#722ed1' }}>{selectedTourTourists.filter(t => t.orderStatus === 'refunded').length}</strong>
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span>
                      已验证: <strong style={{ color: '#52c41a' }}>{selectedTourTourists.filter(t => t.uploadStatus === 'verified').length}</strong>
                    </span>
                    <span>
                      待处理: <strong style={{ color: '#faad14' }}>{selectedTourTourists.filter(t => ['pending', 'uploaded', 'rejected'].includes(t.uploadStatus)).length}</strong>
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        }
        open={touristsModalVisible}
        onCancel={() => {
          setTouristsModalVisible(false);
          setSelectedTourTourists([]);
          setSelectedTourInfo(null);
        }}
        width={selectedTourInfo?.tourType === 'daytour' ? 1000 : 1400}
        footer={[
          selectedTourInfo?.touristInfoLocked && (
            <Alert
              key="locked-alert"
              message="游客信息已锁定，无法添加、修改或删除游客"
              type="warning"
              showIcon
              style={{ marginRight: 'auto', padding: '4px 12px' }}
            />
          ),
          selectedTourInfo?.tourType !== 'daytour' ? (
            <Button
              key="csv"
              icon={<FileTextOutlined />}
              onClick={handleExportCSV}
              disabled={selectedTourTourists.filter(t => t.uploadStatus === 'verified').length === 0}
            >
              CSV导出 ({selectedTourTourists.filter(t => t.uploadStatus === 'verified').length})
            </Button>
          ) : null,
          // 游客一律从 member 同步，护照系统不再录单（2026-08-19 起一日游也一样）：
          // 客人自助在商城下单，或销售在会员后台「代客下单」——两条路共用同一本名额账本，
          // 护照这边只负责出团执行（通知书、导游、上车点、名单）
          null,
          <Button
            key="close"
            onClick={() => setTouristsModalVisible(false)}
          >
            关闭
          </Button>
        ]}
      >
        {selectedTourInfo?.tourType === 'daytour' && (
          <Alert
            type="info" showIcon style={{ marginBottom: 12 }}
            message="一日游游客为只读，姓名、上车点、票种与证件在会员系统修改"
            action={<Button size="small" type="link" href="https://member.wanguo.co.nz/admin/new-order" target="_blank">前往会员系统</Button>}
          />
        )}
        <Table
          className="sales-tourist-table"
          columns={(() => {
            const isDaytour = selectedTourInfo?.tourType === 'daytour';
            const rawCols = [
            {
              title: isDaytour ? '证件姓名' : '护照姓名',
              dataIndex: 'touristName',
              key: 'touristName',
              width: 180,
              fixed: 'left',
              ellipsis: {
                showTitle: false,
              },
              render: (name, record) => {
                const displayName = name || '-';
                // 检查护照有效期是否距出发日期不足6个月
                let expiryWarning = false;
                if (record.passportExpiryDate && selectedTourInfo?.departureDate) {
                  const expiry = moment(record.passportExpiryDate);
                  const departure = moment(selectedTourInfo.departureDate);
                  if (expiry.isValid() && departure.isValid()) {
                    const monthsDiff = expiry.diff(departure, 'months', true);
                    if (monthsDiff < 6) {
                      expiryWarning = true;
                    }
                  }
                }
                return (
                  <Tooltip placement="topLeft" title={displayName}>
                    <span style={{ textTransform: 'uppercase' }}>{displayName}</span>
                    {expiryWarning && (
                      <Tooltip title={`护照有效期不足6个月（${moment(record.passportExpiryDate).format('YYYY-MM-DD')}）`}>
                        <WarningOutlined style={{ color: '#fa8c16', marginLeft: 6, fontSize: 14 }} />
                      </Tooltip>
                    )}
                  </Tooltip>
                );
              },
            },
            ...(selectedTourInfo?.tourType !== 'daytour' ? [{
              title: '规格',
              key: 'spec',
              width: 160,
              ellipsis: false,
              // 规格由 TTOUR 订单推送时确定，只读（与管理端一致）
              render: (_, record) => {
                const pkg = { with_tour: { t: 'Plus款', c: 'blue' }, no_tour: { t: '基础款', c: 'default' } }[record.packageType];
                const tier = { '2star': { t: '二钻', c: 'green' }, '3star': { t: '三钻', c: 'gold' }, '4star': { t: '四钻', c: 'volcano' }, '5star': { t: '五钻', c: 'red' } }[record.hotelTier];
                if (!pkg && !tier) return <span style={{ color: '#999' }}>-</span>;
                return (
                  <Space size={4} style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                    {pkg && <Tag color={pkg.c} style={{ margin: 0 }}>{pkg.t}</Tag>}
                    {tier && <Tag color={tier.c} style={{ margin: 0 }}>{tier.t}</Tag>}
                  </Space>
                );
              },
            }] : []),
            {
              title: 'EKOK',
              dataIndex: 'ekok',
              key: 'ekok',
              width: 80,
              ellipsis: false,
              render: (text, record, index) => {
                // 检查是否与前一行的EKOK相同
                const prevRecord = index > 0 ? selectedTourTourists[index - 1] : null;
                const isSameAsPrev = prevRecord && prevRecord.ekok === text;

                return (
                  <span style={{
                    fontWeight: !isSameAsPrev && text ? 'bold' : 'normal',
                    color: text ? '#1890ff' : '#999',
                    whiteSpace: 'nowrap'
                  }}>
                    {text || '-'}
                  </span>
                );
              },
            },
            ...(selectedTourInfo?.tourType === 'daytour' ? [{
              title: '订单号',
              dataIndex: 'orderNumber',
              key: 'orderNumber',
              width: 130,
              ellipsis: false,
              render: (text) => text ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{text}</span> : '-',
            },
            {
              title: '订单状态',
              dataIndex: 'orderStatus',
              key: 'orderStatus',
              width: 85,
              render: (status) => {
                const statusMap = {
                  'pending': { text: '待确认', color: 'warning' },
                  'confirmed': { text: '已确认', color: 'success' },
                  'cancelled': { text: '已取消', color: 'error' },
                  'refunded': { text: '退款', color: 'purple' },
                };
                const config = statusMap[status];
                if (!config) return '-';
                return <Tag color={config.color}>{config.text}</Tag>;
              },
            }] : []),
            {
              title: '类型',
              dataIndex: 'touristType',
              key: 'touristType',
              width: 60,
              ellipsis: false,
              render: (type, record) => {
                if (selectedTourInfo?.tourType === 'daytour') {
                  return record.discountPrice
                    ? <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>优惠</span>
                    : <span>原价</span>;
                }
                if (type === 'ADT') {
                  return <Tag color="blue">ADT</Tag>;
                } else if (type === 'CHD') {
                  return <Tag color="green">CHD</Tag>;
                }
                return '-';
              },
            },
            ...(selectedTourInfo?.tourType !== 'daytour' ? [{
              title: '护照号码',
              dataIndex: 'passportNumber',
              key: 'documentNumber',
              width: 100,
              ellipsis: false,
              render: (text) => text || '-',
            }] : []),
            ...(selectedTourInfo?.tourType !== 'daytour' ? [
            {
              title: '护照',
              key: 'passportView',
              width: 90,
              render: (_, record) => {
                const reuseTag = <PassportReuseTag reuse={record.passportReuse} />;
                if (record.passportPhoto) {
                  return (
                    <Space direction="vertical" size={2}>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        style={{ padding: 0 }}
                        onClick={() => {
                          setPassportModalRecord(record);
                          setViewWithChinaDoc(false);   // 护照列只看护照
                          setPassportModalVisible(true);
                        }}
                      >
                        查看
                      </Button>
                      {reuseTag}
                    </Space>
                  );
                }
                return record.passportReuse ? reuseTag : <span style={{ color: '#999' }}>-</span>;
              },
            },
            {
              // 中国团附加证件：护照传完之后接着补传身份证/台胞证（与护照同一套代客上传逻辑）
              title: '身份证/台胞证',
              key: 'chinaDoc',
              width: 130,
              render: (_, record) => (
                <ChinaDocAction record={record} onDone={() => handleViewTourists(selectedTourInfo)} />
              ),
            },
            ] : []),
            {
              title: '联系电话',
              key: 'phone',
              width: 130,
              ellipsis: { showTitle: false },
              // 一日游用 phone，出境/入境同步游客用 contactPhone（订单联系人电话）
              render: (_, record) => {
                const text = record.phone || record.contactPhone;
                return text ? <Tooltip placement="topLeft" title={text}>{text}</Tooltip> : '-';
              },
            },
            ...(selectedTourInfo?.tourType === 'daytour' && getTourPickupPoints(selectedTourInfo).length > 0 ? [{
              title: '上车点',
              dataIndex: 'pickupPoint',
              key: 'pickupPoint',
              width: 150,
              ellipsis: false,
              render: (text) => {
                if (!text) return <span style={{ color: '#999' }}>-</span>;
                const points = getTourPickupPoints(selectedTourInfo);
                const found = points.find(p => p.name === text);
                const display = found && found.time ? `${text} (${found.time})` : text;
                return <Tag color="cyan"><CarOutlined /> {display}</Tag>;
              },
            }] : []),
            ...(selectedTourInfo?.tourType !== 'daytour' ? [
            {
              title: '性别',
              dataIndex: 'gender',
              key: 'gender',
              width: 45,
              ellipsis: false,
              render: (gender) => {
                if (!gender) return '-';
                return gender === 'M' ? '男' : '女';
              },
            },
            {
              title: '国籍',
              dataIndex: 'nationality',
              key: 'nationality',
              width: 50,
              ellipsis: false,
              render: (code) => code || '-',
            },
            {
              title: '出生地',
              dataIndex: 'birthPlace',
              key: 'birthPlace',
              width: 110,
              ellipsis: { showTitle: false },
              render: (text) => text ? <Tooltip placement="topLeft" title={text}>{text}</Tooltip> : '-',
            },
            {
              title: '房型',
              dataIndex: 'roomType',
              key: 'roomType',
              width: 220,
              // 房型由 TTOUR 订单同步，销售端只读不可改；完整显示（含拼房订单号）
              render: (roomType) => roomType
                ? <span style={{ wordBreak: 'break-all' }}>{roomType}</span>
                : <span style={{ color: '#999' }}>-</span>,
            },
            ] : []),
            // 仅非日游产品显示 Add-Ons 列
            ...(selectedTourInfo?.tourType !== 'daytour' ? [
              {
                title: 'Add-Ons产品',
                dataIndex: 'dayTrips',
                key: 'addOns',
                width: 250,
                ellipsis: false,
                render: (dayTrips) => {
                  if (!dayTrips || availableAddOns.length === 0) return <span style={{ color: '#999' }}>无</span>;

                  // 颜色循环列表
                  const colors = ['blue', 'purple', 'magenta', 'green', 'cyan', 'orange', 'gold', 'lime'];

                  // 使用动态Add-On列表
                  const purchased = availableAddOns.filter(addOn => {
                    const key = `addon_${addOn.id}`;
                    return dayTrips[key] === true;
                  });

                  if (purchased.length === 0) {
                    return <span style={{ color: '#999' }}>无</span>;
                  }

                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {purchased.map((addOn, index) => (
                        <Tag key={addOn.id} color={colors[index % colors.length]} style={{ margin: 0 }}>
                          {addOn.name}
                        </Tag>
                      ))}
                    </div>
                  );
                },
              },
              // 动态生成Add-On选择列
              ...availableAddOns.map((addon) => ({
                title: addon.name,
                dataIndex: 'dayTrips',
                key: `addon_${addon.id}`,
                width: Math.max(110, addon.name.length * 10 + 40),
                ellipsis: false,
                render: (dayTrips, record) => {
                  const addonKey = `addon_${addon.id}`;
                  const currentValue = dayTrips?.[addonKey] || false;
                  const isLocked = selectedTourInfo?.touristInfoLocked;
                  return (
                    <Select
                      value={currentValue}
                      style={{ width: '100%' }}
                      onChange={(value) => handleUpdateSingleAddOn(record.id, addonKey, value)}
                      size="small"
                      disabled={isLocked}
                    >
                      <Option value={false}>No</Option>
                      <Option value={true}>Yes</Option>
                    </Select>
                  );
                },
              })),
            ] : []),
            {
              title: '操作',
              key: 'action',
              width: 140,
              fixed: 'right',
              render: (_, record) => {
                const isLocked = selectedTourInfo?.touristInfoLocked;
                const isCancelled = record.orderStatus === 'cancelled' || record.orderStatus === 'refunded';
                // 一日游游客只读：姓名 / 上车点 / 票种 / 证件都在会员系统改，改完推过来（2026-10-22）
                const daytourRO = selectedTourInfo?.tourType === 'daytour';
                return (
                  <Space size={4}>
                    {daytourRO && (record.passportPhoto || record.documentPhoto) && (
                      <Tooltip title="查看证件">
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => {
                            setPassportModalRecord(record);
                            setViewWithChinaDoc(true);
                            setPassportModalVisible(true);
                          }}
                        />
                      </Tooltip>
                    )}
                    {selectedTourInfo?.tourType !== 'daytour' && (
                      !(record.passportPhoto || record.documentPhoto) ? (
                        <Tooltip title={isCancelled ? "订单已取消" : isLocked ? "游客信息已锁定" : "上传护照"}>
                          <Button
                            size="small"
                            type="primary"
                            icon={<UploadOutlined />}
                            onClick={() => handleUploadPassport(record)}
                            disabled={isLocked || isCancelled}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="查看护照">
                          <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setPassportModalRecord(record);
                              setViewWithChinaDoc(true);   // 操作列的眼睛看全部证件
                              setPassportModalVisible(true);
                            }}
                          />
                        </Tooltip>
                      )
                    )}
                    {!daytourRO && record.uploadStatus !== 'verified' && !isCancelled && (
                      <Tooltip title="发给客人">
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyUploadLink(record.uploadLink)}
                        />
                      </Tooltip>
                    )}
                    {!daytourRO && (
                      <Tooltip title={isCancelled ? "订单已取消" : isLocked ? "游客信息已锁定" : "编辑"}>
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEditTourist(record)}
                          disabled={isLocked || isCancelled}
                        />
                      </Tooltip>
                    )}
                    {!isCancelled && selectedTourInfo?.tourType !== 'daytour' && (
                      <Tooltip title="核销 Voucher（用兑换码抵扣）">
                        <Button
                          size="small"
                          icon={<GiftOutlined />}
                          style={{ color: '#1a5490', borderColor: '#1a5490' }}
                          onClick={() => {
                            setRedeemingTourist(record);
                            setRedeemModalVisible(true);
                          }}
                        />
                      </Tooltip>
                    )}
                    {!isCancelled && (
                      <Tooltip title="备注">
                        <Button
                          size="small"
                          icon={<FileTextOutlined />}
                          onClick={() => handleEditRemarks(record)}
                        />
                      </Tooltip>
                    )}
                    {selectedTourInfo?.tourType !== 'daytour' && (
                      <Tooltip title={isLocked ? "游客信息已锁定" : "删除"}>
                        <Popconfirm
                          title="确定删除这个游客吗？"
                          description="删除后将无法恢复"
                          icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                          onConfirm={() => handleDeleteTourist(record.id)}
                          okText="确定"
                          cancelText="取消"
                          disabled={isLocked}
                        >
                          <Button size="small" danger icon={<DeleteOutlined />} disabled={isLocked} />
                        </Popconfirm>
                      </Tooltip>
                    )}
                  </Space>
                );
              },
            },
          ];
            // 标准团只显示高频列（其余在展开行看）；一日游去掉组头里已有的列。两者都用订单分组表头。
            const STANDARD_KEYS = ['touristName', 'spec', 'documentNumber', 'passportView', 'chinaDoc', 'roomType', 'action'];
            const filtered = isDaytour
              ? rawCols.filter(c => c.key !== 'orderNumber' && c.key !== 'phone' && c.key !== 'ekok')
              : rawCols.filter(c => STANDARD_KEYS.includes(c.key));
            return filtered.map((col, idx) => ({
              ...col,
              fixed: undefined,
              onCell: (record, rowIndex) => {
                if (record._isGroupHeader) {
                  return idx === 0
                    ? { colSpan: filtered.length, style: { background: '#fafafa', padding: '6px 16px', borderBottom: '1px solid #f0f0f0' } }
                    : { colSpan: 0 };
                }
                const base = typeof col.onCell === 'function' ? col.onCell(record, rowIndex) : {};
                // 操作列点击不触发整行展开
                if (col.key === 'action') return { ...base, onClick: (e) => e.stopPropagation() };
                return base;
              },
              render: (...args) => {
                const record = args[1];
                if (record._isGroupHeader) {
                  if (idx === 0) {
                    return (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: '#888' }}>订单号:</span>{' '}
                        <b style={{ fontFamily: 'monospace', color: '#262626' }}>{record.orderNumber}</b>
                        {record.ekok && (
                          <>
                            <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                            <span style={{ color: '#888' }}>EKOK:</span>{' '}
                            <b style={{ color: '#1890ff' }}>{record.ekok}</b>
                          </>
                        )}
                        <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#888' }}>联系人:</span>{' '}
                        <b style={{ color: '#262626', textTransform: 'uppercase' }}>{record._mainContactName}</b>
                        <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#888' }}>电话:</span>{' '}
                        <b style={{ color: '#262626' }}>{record._mainContactPhone || '-'}</b>
                        <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#888' }}>{record._groupSize}人</span>
                        {record._confirmedCount > 0 && <Tag color="success" style={{ marginLeft: 6 }}>已确认 {record._confirmedCount}</Tag>}
                        {record._pendingCount > 0 && <Tag color="warning" style={{ marginLeft: 4 }}>待确认 {record._pendingCount}</Tag>}
                        {record._cancelledCount > 0 && <Tag color="error" style={{ marginLeft: 4 }}>已取消 {record._cancelledCount}</Tag>}
                        {record._refundedCount > 0 && <Tag color="purple" style={{ marginLeft: 4 }}>退款 {record._refundedCount}</Tag>}
                      </div>
                    );
                  }
                  return null;
                }
                return typeof col.render === 'function' ? col.render(...args) : (args[0] ?? '-');
              },
            }));
          })()}
          dataSource={(() => {
            // 统一按订单分组：一日游按订单号；标准团按 EKOK（同 EKOK 的多个同步订单合并一组）
            const isDaytourGrouping = selectedTourInfo?.tourType === 'daytour';
            const serialOf = (t) => t.ttourOrderSerial || t.orderNumber || '';
            const keyOf = (t) => isDaytourGrouping
              ? (t.orderNumber || t.ttourOrderSerial || `_solo_${t.id}`)
              : (t.ekok || serialOf(t) || `_solo_${t.id}`);
            const sorted = [...selectedTourTourists].sort((a, b) =>
              keyOf(a).localeCompare(keyOf(b)) || serialOf(a).localeCompare(serialOf(b)) || (a.id - b.id)
            );
            const groups = {};
            let groupIdx = 0;
            sorted.forEach((t) => {
              const key = keyOf(t);
              if (!groups[key]) {
                groups[key] = { count: 0, firstId: t.id, groupIndex: groupIdx++, members: [] };
              }
              groups[key].count++;
              groups[key].members.push(t);
            });
            // Build result with header rows inserted before each group
            const result = [];
            let posInGroup = 0;
            let lastKey = null;
            sorted.forEach((t) => {
              const key = keyOf(t);
              const g = groups[key];
              if (key !== lastKey) {
                posInGroup = 0;
                lastKey = key;
                // Insert group header row
                const mainContact = g.members[0];
                const confirmed = g.members.filter(m => m.orderStatus === 'confirmed').length;
                const pending = g.members.filter(m => m.orderStatus === 'pending').length;
                const cancelled = g.members.filter(m => m.orderStatus === 'cancelled').length;
                const refunded = g.members.filter(m => m.orderStatus === 'refunded').length;
                const orderNumbers = [...new Set(g.members.map(m => (isDaytourGrouping ? (m.orderNumber || m.ttourOrderSerial) : serialOf(m))).filter(Boolean))];
                const phones = [...new Set(g.members.map(m => m.contactPhone || m.phone).filter(Boolean))];
                result.push({
                  _isGroupHeader: true,
                  id: `header_${key}`,
                  orderNumber: orderNumbers.length ? orderNumbers.join('、') : '-',
                  ekok: mainContact.ekok || '',
                  _mainContactName: mainContact.touristName || '-',
                  _mainContactPhone: phones.join(' / '),
                  _groupSize: g.count,
                  _confirmedCount: confirmed,
                  _pendingCount: pending,
                  _cancelledCount: cancelled,
                  _refundedCount: refunded,
                  _groupIndex: g.groupIndex,
                });
              }
              const isFirst = g.firstId === t.id;
              t._ekokRowSpan = isFirst ? g.count : 0;
              t._isLastInGroup = posInGroup === g.count - 1;
              t._groupIndex = g.groupIndex;
              posInGroup++;
              result.push(t);
            });
            return result;
          })()}
          rowKey="id"
          loading={touristsLoading}
          pagination={false}
          size="small"
          scroll={{ x: selectedTourInfo?.tourType === 'daytour' ? undefined : 900 }}
          expandable={selectedTourInfo?.tourType !== 'daytour' ? {
            columnWidth: 32,
            expandRowByClick: true,
            rowExpandable: (record) => !record._isGroupHeader,
            // 展开行展示被收起的列，复用各列原有 render（含 Add-Ons 的编辑下拉）
            expandedRowRender: (record) => {
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '8px 24px', padding: '4px 8px 4px 24px', fontSize: 12.5 }}>
                  <div><span style={{ color: '#999', marginRight: 8 }}>类型</span>{record.touristType || '-'}</div>
                  <div><span style={{ color: '#999', marginRight: 8 }}>联系电话</span>{record.phone || record.contactPhone || '-'}</div>
                  <div><span style={{ color: '#999', marginRight: 8 }}>性别</span>{record.gender === 'M' ? '男' : record.gender === 'F' ? '女' : '-'}</div>
                  <div><span style={{ color: '#999', marginRight: 8 }}>国籍</span>{record.nationality || '-'}</div>
                  <div><span style={{ color: '#999', marginRight: 8 }}>出生地</span>{record.birthPlace || '-'}</div>
                  <div><span style={{ color: '#999', marginRight: 8 }}>出生日期</span>{record.passportBirthDate ? moment(record.passportBirthDate).format('YYYY-MM-DD') : '-'}</div>
                  <div><span style={{ color: '#999', marginRight: 8 }}>签发日期</span>{record.passportIssueDate ? moment(record.passportIssueDate).format('YYYY-MM-DD') : '-'}</div>
                  <div><span style={{ color: '#999', marginRight: 8 }}>有效期</span>{record.passportExpiryDate ? moment(record.passportExpiryDate).format('YYYY-MM-DD') : '-'}</div>
                  <div><span style={{ color: '#999', marginRight: 8 }}>备注</span>{record.remarks || '-'}</div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={{ color: '#999', marginRight: 8 }}>Add-Ons</span>
                    {(() => {
                      const purchased = availableAddOns.filter(a => record.dayTrips?.[`addon_${a.id}`] === true);
                      return purchased.length
                        ? purchased.map(a => <Tag key={a.id} color="blue" style={{ marginRight: 4 }}>{a.name}</Tag>)
                        : <span style={{ color: '#999' }}>无</span>;
                    })()}
                  </div>
                </div>
              );
            },
          } : undefined}
          onRow={(record) => {
            if (record._isGroupHeader) {
              return { style: { height: '36px', background: '#fafafa', cursor: 'default' } };
            }
            return {
              style: {
                ...(record._isLastInGroup ? { borderBottom: '2px solid #e0e0e0' } : {}),
                ...(selectedTourInfo?.tourType !== 'daytour' ? { cursor: 'pointer' } : {}),
              }
            };
          }}
          locale={{
            emptyText: (
              <Empty
                description={selectedTourInfo?.tourType === 'daytour'
                  ? (selectedTourInfo?.touristInfoLocked ? "暂无游客（信息已锁定）" : "暂无游客")
                  : "暂无游客（游客从 member 系统同步）"}
                style={{ padding: '20px 0' }}
              >
                {/* 仅一日游允许现场登记；出境/入境从 member 同步 */}
                {selectedTourInfo?.tourType === 'daytour' ? (
                  <span style={{ color: '#999' }}>请在会员系统「代客下单」录入客人</span>
                ) : null}
              </Empty>
            )
          }}
        />
      </Modal>

      {/* 添加游客弹窗 */}
      <Modal
        title="添加游客"
        open={addTouristModalVisible}
        onCancel={() => {
          setAddTouristModalVisible(false);
          form.resetFields();
          setMainContactIndex(0);
          setPendingDocs({});
        }}
        footer={null}
        width={selectedTourInfo?.tourType === 'daytour' ? 960 : 600}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddTourist}
          initialValues={selectedTourInfo?.tourType === 'daytour' ? { tourists: [{}, {}] } : {}}
        >
          {selectedTourInfo?.tourType === 'daytour' ? (
            <>
              {/* 一日游：团体订单模式 */}
              <Form.Item
                name="ekok"
                label="EKOK"
                rules={[{ required: true, message: '请输入EKOK编号' }]}
                style={{ marginBottom: 16 }}
              >
                <Input
                  placeholder="请输入EKOK编号"
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    form.setFieldValue('ekok', val);
                  }}
                />
              </Form.Item>

              <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
                游客列表
                <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>点击 <UserOutlined /> 设为主要联系人</span>
              </div>

              <Form.List name="tourists">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => {
                      const hasPickup = getTourPickupPoints(selectedTourInfo).length > 0;
                      const isMain = mainContactIndex === index;
                      return (
                        <Row key={key} gutter={8} align="middle" style={{
                          marginBottom: 8,
                          background: isMain ? '#e6f7ff' : 'transparent',
                          borderRadius: 4,
                          padding: '2px 0'
                        }}>
                          <Col flex="none">
                            <Tooltip title={isMain ? '主要联系人' : '设为主要联系人'}>
                              <Button
                                type="text"
                                size="small"
                                icon={<UserOutlined />}
                                onClick={() => setMainContactIndex(index)}
                                style={{
                                  color: isMain ? '#1890ff' : '#d9d9d9',
                                  fontSize: 14
                                }}
                              />
                            </Tooltip>
                          </Col>
                          <Col flex="1" style={{ minWidth: 0 }}>
                            <Form.Item
                              {...restField}
                              name={[name, 'touristName']}
                              rules={[
                                { required: true, message: '姓名' },
                                {
                                  validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    const validation = validatePassportName(value);
                                    return validation.valid ? Promise.resolve() : Promise.reject(new Error(validation.error));
                                  }
                                }
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                placeholder="姓/名 (ZHANG/SAN)"
                                style={{ textTransform: 'uppercase' }}
                                onChange={(e) => {
                                  const val = e.target.value.toUpperCase();
                                  form.setFieldValue(['tourists', name, 'touristName'], val);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex="150px">
                            <Form.Item
                              {...restField}
                              name={[name, 'phone']}
                              rules={isMain ? [{ required: true, message: '电话' }] : []}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder={isMain ? '电话（必填）' : '电话（选填）'} />
                            </Form.Item>
                          </Col>
                          {hasPickup && (
                            <Col style={{ width: 200, flex: '0 0 200px' }}>
                              <Form.Item
                                {...restField}
                                name={[name, 'pickupPoint']}
                                style={{ marginBottom: 0 }}
                              >
                                <Select placeholder="上车点" allowClear size="middle" style={{ width: '100%' }}>
                                  {getTourPickupPoints(selectedTourInfo).map((p, i) => (
                                    <Option key={i} value={p.name}>{p.name}{p.time ? ` (${p.time})` : ''}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                          )}
                          <Col flex="none">
                            <Form.Item
                              {...restField}
                              name={[name, 'discountPrice']}
                              valuePropName="checked"
                              style={{ marginBottom: 0 }}
                            >
                              <Checkbox>优惠价(55岁以上)</Checkbox>
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.tourists?.[name]?.discountPrice !== cur?.tourists?.[name]?.discountPrice}>
                              {({ getFieldValue }) => {
                                const checked = getFieldValue(['tourists', name, 'discountPrice']);
                                if (!checked) return null;
                                const doc = pendingDocs[index];
                                const status = doc?.status || 'idle';

                                if (status === 'ocr') {
                                  return <Button size="small" loading style={{ fontSize: 12 }}>识别中</Button>;
                                }
                                if (status === 'done') {
                                  return (
                                    <Button
                                      size="small"
                                      icon={<EyeOutlined />}
                                      style={{
                                        background: '#52c41a',
                                        borderColor: '#52c41a',
                                        color: '#fff', fontSize: 12
                                      }}
                                      onClick={() => { setPreviewDocUrl(doc.previewUrl); setPreviewDocVisible(true); }}
                                    >
                                      核对证件
                                    </Button>
                                  );
                                }
                                return (
                                  <Upload
                                    accept="image/jpeg,image/jpg,image/png"
                                    showUploadList={false}
                                    beforeUpload={async (file) => {
                                      if (!await validatePassportFile(file)) return false;
                                      const previewUrl = URL.createObjectURL(file);
                                      setPendingDocs(prev => ({ ...prev, [index]: { file, status: 'ocr', previewUrl } }));
                                      try {
                                        const fd = new FormData();
                                        fd.append('document', file);
                                        fd.append('documentType', 'passport');
                                        fd.append('daytour', 'true');
                                        const res = await axios.post(`${API_BASE}/tourists/ocr-document`, fd, {
                                          headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
                                        });
                                        if (res.data.success && res.data.data.fullName) {
                                          form.setFieldValue(['tourists', name, 'touristName'], res.data.data.fullName);
                                        }
                                        setPendingDocs(prev => ({ ...prev, [index]: { file, status: 'done', ocrData: res.data.data, previewUrl } }));
                                      } catch (err) {
                                        message.error(err.response?.data?.error || '证件识别失败');
                                        setPendingDocs(prev => ({ ...prev, [index]: { file, status: 'error', previewUrl } }));
                                      }
                                      return false;
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      icon={status === 'error' ? <UploadOutlined /> : <UploadOutlined />}
                                      style={{ borderColor: status === 'error' ? '#ff4d4f' : '#1890ff', color: status === 'error' ? '#ff4d4f' : '#1890ff', fontSize: 12 }}
                                    >
                                      {status === 'error' ? '重新上传' : '上传证件'}
                                    </Button>
                                  </Upload>
                                );
                              }}
                            </Form.Item>
                          </Col>
                          <Col flex="none">
                            {fields.length > 1 ? (
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  remove(name);
                                  // 删除后修正主要联系人索引
                                  if (index < mainContactIndex) {
                                    setMainContactIndex(prev => prev - 1);
                                  } else if (index === mainContactIndex) {
                                    setMainContactIndex(0);
                                  }
                                  // 删除后清理并重新映射 pendingDocs
                                  setPendingDocs(prev => {
                                    const updated = {};
                                    Object.keys(prev).forEach(k => {
                                      const ki = parseInt(k);
                                      if (ki < index) updated[ki] = prev[k];
                                      else if (ki > index) updated[ki - 1] = prev[k];
                                      // ki === index 的直接丢弃
                                    });
                                    return updated;
                                  });
                                }}
                              />
                            ) : <div style={{ width: 24 }} />}
                          </Col>
                        </Row>
                      );
                    })}
                    <Form.Item style={{ marginBottom: 16 }}>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ height: 32 }}>
                        继续添加游客
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<TeamOutlined />} loading={submittingOrder}>
                    {submittingOrder ? '提交中...' : '提交订单'}
                  </Button>
                  <Button onClick={() => {
                    setAddTouristModalVisible(false);
                    form.resetFields();
                  }} disabled={submittingOrder}>
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </>
          ) : (
            <>
              {/* 出境游：单人添加模式（不变） */}
              <Form.Item
                name="touristName"
                label="游客姓名"
                rules={[
                  { required: true, message: '请输入游客姓名' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const validation = validatePassportName(value);
                      if (validation.valid) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(validation.error));
                    }
                  }
                ]}
                tooltip={getNameFormatHint()}
                extra="格式：姓/名（纯英文），例如：ZHANG/SAN"
              >
                <Input
                  placeholder="姓/名 (例: ZHANG/SAN)"
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    form.setFieldsValue({ touristName: value });
                  }}
                />
              </Form.Item>

              <Form.Item
                name="ekok"
                label="EKOK"
                rules={[{ required: true, message: '请输入EKOK编号' }]}
              >
                <Input
                  placeholder="请输入EKOK编号"
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>

              <Form.Item
                name="contactPhone"
                label="联系电话"
              >
                <Input placeholder="选填，例如 0211234567" inputMode="tel" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                    添加游客
                  </Button>
                  <Button onClick={() => {
                    setAddTouristModalVisible(false);
                    form.resetFields();
                  }}>
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* 编辑游客弹窗 */}
      {editingTourist && (
        <Modal
          title="编辑游客信息"
          open={!!editingTourist}
          onCancel={() => {
            setEditingTourist(null);
            setPassportExpiryWarning(null);
            setPassportValidityWarning(null);
            setNzPassportWarning(null);
            editForm.resetFields();
          }}
          footer={null}
          width={900}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleUpdateTourist}
          >
            {/* ========== 一日游编辑表单 ========== */}
            {selectedTourInfo?.tourType === 'daytour' ? (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="touristName"
                      label="姓名"
                      rules={[
                        { required: true, message: '请输入姓名' },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const validation = validatePassportName(value);
                            if (validation.valid) return Promise.resolve();
                            return Promise.reject(new Error(validation.error));
                          }
                        }
                      ]}
                      tooltip={getNameFormatHint()}
                      extra={editingTourist?.orderStatus === 'confirmed' ? <span style={{ color: '#ff4d4f', fontSize: 12 }}>已确认状态下不可修改姓名</span> : undefined}
                    >
                      <Input
                        placeholder="姓/名 (例: ZHANG/SAN)"
                        style={{ textTransform: 'uppercase' }}
                        disabled={editingTourist?.orderStatus === 'confirmed'}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          editForm.setFieldsValue({ touristName: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="ekok" label="EKOK">
                      <Input placeholder="EKOK编号" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="phone" label="联系电话">
                      <Input placeholder="联系电话" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="discountPrice" valuePropName="checked" style={{ marginBottom: 0 }}>
                      <Checkbox style={{ marginTop: 30 }}>优惠价(55岁以上)</Checkbox>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.discountPrice !== cur?.discountPrice}>
                      {({ getFieldValue }) => {
                        const checked = getFieldValue('discountPrice');
                        return checked ? <span style={{ color: '#ff4d4f', fontSize: 11 }}>8小时内需上传证件，过时取消</span> : null;
                      }}
                    </Form.Item>
                  </Col>
                </Row>

                {getTourPickupPoints(selectedTourInfo).length > 0 && (
                  <Form.Item name="pickupPoint" label="上车点">
                    <Select placeholder="选择上车点" allowClear>
                      {getTourPickupPoints(selectedTourInfo).map((p, i) => (
                        <Option key={i} value={p.name}>{p.name}{p.time ? ` (${p.time})` : ''}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {editingTourist?.documentType && (
                  <>
                    <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, fontSize: 13, color: '#1d39c4' }}>
                      证件类型：{editingTourist.documentType === 'passport' ? '护照' : editingTourist.documentType === 'nz_driver_license' ? '新西兰驾照' : 'SuperGold Card'}
                    </div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="documentNumber" label="证件号码">
                          <Input placeholder="证件号码" style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>
                      {editingTourist.documentType !== 'nz_senior_card' && (
                        <Col span={12}>
                          <Form.Item name="birthDate" label="出生日期">
                            <Input placeholder="YYYY-MM-DD" type="date" />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  </>
                )}

                {/* 证件照片 */}
                {editingTourist?.documentPhoto && (
                  <Form.Item label="证件照片">
                    <Image
                      src={
                        editingTourist.documentPhoto.startsWith('http')
                          ? editingTourist.documentPhoto
                          : `${API_BASE.replace('/api', '')}${editingTourist.documentPhoto}`
                      }
                      alt="证件照片"
                      width={200}
                      height={120}
                      style={{ objectFit: 'cover', border: '1px solid #d9d9d9', borderRadius: 4 }}
                    />
                  </Form.Item>
                )}
              </>
            ) : (
              <>
                {/* ========== 出境游编辑表单 ========== */}
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="touristName"
                      label="护照姓名"
                      rules={[
                        { required: true, message: '请输入护照姓名' },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const validation = validatePassportName(value);
                            if (validation.valid) return Promise.resolve();
                            return Promise.reject(new Error(validation.error));
                          }
                        }
                      ]}
                      tooltip={getNameFormatHint()}
                      extra="上传护照后会自动更新为识别的姓名"
                    >
                      <Input
                        placeholder="姓/名 (例: ZHANG/SAN)"
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          editForm.setFieldsValue({ touristName: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="passportNumber" label="护照号码">
                      <Input placeholder="护照号码" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="nationality" label="国籍">
                      <Select
                        placeholder="选择国籍"
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {getAllCountries().map(country => (
                          <Option key={country.value} value={country.value} label={country.label}>
                            {country.value} - {country.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="gender" label="性别">
                      <Select placeholder="选择性别">
                        <Option value="M">男</Option>
                        <Option value="F">女</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="passportBirthDate" label="出生日期">
                      <Input placeholder="YYYY-MM-DD" type="date" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="passportIssueDate" label="签发日期">
                      <Input placeholder="YYYY-MM-DD" type="date" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="passportExpiryDate" label="护照有效期">
                      <Input
                        placeholder="YYYY-MM-DD"
                        type="date"
                        onChange={(e) => checkPassportExpiry(e.target.value)}
                      />
                    </Form.Item>
                    {passportExpiryWarning && (
                      <Alert
                        message="护照有效期不足6个月"
                        description={
                          <div>
                            <p style={{ margin: 0 }}>
                              护照有效期（<strong>{passportExpiryWarning.expiryDate}</strong>）距离出团日期（<strong>{passportExpiryWarning.departureDate}</strong>）
                              仅剩 <strong style={{ color: '#ff4d4f' }}>{passportExpiryWarning.monthsDiff} 个月</strong>，不足6个月。
                            </p>
                            <p style={{ margin: '8px 0 0 0', color: '#faad14' }}>建议客人及时更新护照，以免影响出行。</p>
                          </div>
                        }
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16, marginTop: -8 }}
                      />
                    )}
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="birthPlace"
                      label="出生地"
                      rules={[
                        { required: true, message: '请输入出生地' },
                        {
                          pattern: /^[A-Za-z\s-]+$/,
                          message: '出生地只能包含英文字母、空格和横线'
                        }
                      ]}
                      extra="请使用英文格式，例如：BEIJING"
                    >
                      <Input
                        placeholder="出生地（英文）"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="ekok" label="EKOK">
                      <Input placeholder="EKOK编号" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="roomType" label="房型">
                      <Select placeholder="选择房型">
                        <Option value="">未分配</Option>
                        <Option value="单人间">单人间</Option>
                        <Option value="大床间">大床间</Option>
                        <Option value="双床间">双床间</Option>
                        <Option value="三人间">三人间</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="contactPhone" label="联系电话">
                      <Input placeholder="联系电话" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="contactEmail"
                      label="联系邮箱"
                      rules={[{ type: 'email', message: '邮箱格式不正确' }]}
                    >
                      <Input placeholder="联系邮箱" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="护照照片">
                  <div>
                    {editingTourist?.passportPhoto && (
                      <div style={{ marginBottom: 12 }}>
                        <Image
                          src={
                            editingTourist.passportPhoto.startsWith('http')
                              ? editingTourist.passportPhoto
                              : `${API_BASE.replace('/api', '')}${editingTourist.passportPhoto}`
                          }
                          alt="当前护照照片"
                          width={200}
                          height={120}
                          style={{ objectFit: 'cover', border: '1px solid #d9d9d9', borderRadius: 4 }}
                        />
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>当前护照照片</div>
                      </div>
                    )}
                    {newPassportPhoto && (
                      <div style={{ marginBottom: 12 }}>
                        <Image
                          src={
                            newPassportPhoto.startsWith('http')
                              ? newPassportPhoto
                              : `${API_BASE.replace('/api', '')}${newPassportPhoto}`
                          }
                          alt="新护照照片"
                          width={200}
                          height={120}
                          style={{ objectFit: 'cover', border: '2px solid #52c41a', borderRadius: 4 }}
                        />
                        <div style={{ fontSize: 12, color: '#52c41a', marginTop: 4 }}>新上传的护照照片</div>
                      </div>
                    )}
                    <Upload
                      name="passport"
                      showUploadList={false}
                      beforeUpload={handlePassportUpload}
                      accept="image/*"
                      disabled={uploadingPhoto}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingPhoto}
                        disabled={uploadingPhoto}
                      >
                        {uploadingPhoto ? '上传中...' : '上传新护照照片'}
                      </Button>
                    </Upload>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                      上传新的护照照片将自动识别并更新表单信息
                    </div>
                  </div>
                </Form.Item>

                {/* 中国团附加证件：护照传完接着补身份证/台胞证 */}
                {editingTourist?.chinaDocRequired && (
                  <Form.Item label="身份证 / 台胞证">
                    <ChinaDocAction
                      mode="block"
                      record={editingTourist}
                      // 只刷新列表，不关闭编辑弹窗 —— 否则销售填到一半的表单会丢
                      onDone={() => handleViewTourists(selectedTourInfo)}
                    />
                  </Form.Item>
                )}
              </>
            )}
            
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<EditOutlined />}>
                  更新信息
                </Button>
                <Button onClick={() => {
                  setEditingTourist(null);
                  editForm.resetFields();
                }}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* 备注编辑弹窗 */}
      <Modal
        title="编辑备注"
        open={remarksModalVisible}
        onCancel={() => {
          setRemarksModalVisible(false);
          setEditingRemarks(null);
          remarksForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={remarksForm}
          layout="vertical"
          onFinish={handleUpdateRemarks}
        >
          <Form.Item
            name="remarks"
            label="备注信息"
          >
            <Input.TextArea 
              rows={4}
              placeholder="请输入备注信息"
              maxLength={500}
              showCount
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<EditOutlined />}>
                保存备注
              </Button>
              <Button onClick={() => {
                setRemarksModalVisible(false);
                setEditingRemarks(null);
                remarksForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 护照/证件照片查看弹窗 */}
      <Modal
        title={`${selectedTourInfo?.tourType === 'daytour' ? '证件'
          : (viewWithChinaDoc && passportModalRecord?.chinaDocPhoto
            ? `护照 / ${getChinaDocLabel(passportModalRecord) || '附加证件'}`
            : '护照')}照片 - ${passportModalRecord?.touristName || ''}`}
        open={passportModalVisible}
        onCancel={() => {
          setPassportModalVisible(false);
          setPassportModalRecord(null);
        }}
        footer={
          !selectedTourInfo?.touristInfoLocked && (
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => {
                setPassportModalVisible(false);
                const record = passportModalRecord;
                setPassportModalRecord(null);
                handleUploadPassport(record);
              }}
            >
              更新{selectedTourInfo?.tourType === 'daytour' ? '证件' : '护照'}
            </Button>
          )
        }
        width={700}
      >
        {passportModalRecord && (() => {
          const isDaytourModal = selectedTourInfo?.tourType === 'daytour';
          const photoUrl = isDaytourModal
            ? (passportModalRecord.documentPhoto || passportModalRecord.passportPhoto)
            : passportModalRecord.passportPhoto;
          return (
            /* 左图右信息：上下堆叠会把弹窗拉得很高，护照本身是横版图，左右排更省空间 */
            <Row gutter={16}>
              <Col span={14}>
                {photoUrl ? (
                  <Image
                    src={photoUrl.startsWith('http') ? photoUrl : `${API_BASE.replace('/api', '')}${photoUrl}`}
                    alt={isDaytourModal ? '证件照片' : '护照照片'}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无照片</div>
                )}
              </Col>
              <Col span={10}>
                <div style={{ padding: '8px 0', lineHeight: 2 }}>
                  <div><strong>姓名：</strong>{passportModalRecord.touristName || '-'}</div>
                  <div><strong>{isDaytourModal ? '证件号码' : '护照号码'}：</strong>{(isDaytourModal ? passportModalRecord.documentNumber : passportModalRecord.passportNumber) || '-'}</div>
                  {isDaytourModal ? (
                    <>
                      <div><strong>出生日期：</strong>{passportModalRecord.birthDate ? moment(passportModalRecord.birthDate).format('YYYY-MM-DD') : '-'}</div>
                      <div><strong>联系电话：</strong>{passportModalRecord.phone || '-'}</div>
                    </>
                  ) : (
                    <>
                      <div><strong>国籍：</strong>{passportModalRecord.nationality || '-'}</div>
                      <div><strong>性别：</strong>{passportModalRecord.gender === 'M' ? '男' : passportModalRecord.gender === 'F' ? '女' : '-'}</div>
                      <div><strong>出生日期：</strong>{passportModalRecord.passportBirthDate ? moment(passportModalRecord.passportBirthDate).format('YYYY-MM-DD') : '-'}</div>
                      <div><strong>签发日期：</strong>{passportModalRecord.passportIssueDate ? moment(passportModalRecord.passportIssueDate).format('YYYY-MM-DD') : '-'}</div>
                      <div><strong>有效期至：</strong>{passportModalRecord.passportExpiryDate ? moment(passportModalRecord.passportExpiryDate).format('YYYY-MM-DD') : '-'}</div>
                    </>
                  )}
                </div>
              </Col>
              {/* 从操作列的眼睛进来时，护照下面接着排身份证/台胞证，同样左图右信息 */}
              {viewWithChinaDoc && !isDaytourModal && passportModalRecord.chinaDocPhoto && (
                <>
                  <Col span={24}>
                    <Divider orientation="left" plain style={{ margin: '16px 0 8px' }}>
                      {getChinaDocLabel(passportModalRecord) || '附加证件'}
                    </Divider>
                  </Col>
                  <Col span={14}>
                    <Image
                      src={passportModalRecord.chinaDocPhoto.startsWith('http')
                        ? passportModalRecord.chinaDocPhoto
                        : `${API_BASE.replace('/api', '')}${passportModalRecord.chinaDocPhoto}`}
                      alt={getChinaDocLabel(passportModalRecord) || '附加证件'}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={10}>
                    <div style={{ padding: '8px 0', lineHeight: 2 }}>
                      <div><strong>证件类型：</strong>{getChinaDocLabel(passportModalRecord) || '-'}</div>
                      <div><strong>证件姓名：</strong>{passportModalRecord.chinaDocName || '-'}</div>
                      <div><strong>证件号码：</strong>{passportModalRecord.chinaDocNumber || '-'}</div>
                      <div><strong>有效期至：</strong>{passportModalRecord.chinaDocExpiryDate ? moment(passportModalRecord.chinaDocExpiryDate).format('YYYY-MM-DD') : '-'}</div>
                    </div>
                  </Col>
                </>
              )}
            </Row>
          );
        })()}
      </Modal>

      {/* 上传护照/证件弹窗 - 完整流程 */}
      <Modal
        title={`上传${selectedTourInfo?.tourType === 'daytour' ? '证件' : '护照'} - ${uploadingTourist?.touristName || ''}`}
        open={uploadModalVisible}
        onCancel={() => {
          if (!uploadingPassport && !confirming) {
            setUploadModalVisible(false);
            setUploadingTourist(null);
            setCurrentStep(0);
            setTempImageUrl(null);
            setTempImageFile(null);
            setRecognizedData(null);
            setDocumentType('passport');
            setUploadDocumentType('passport');
            setEmailVerified(false);
            setOcrNameMismatch('');
            setPassportValidityWarning(null);
            setNzPassportWarning(null);
            setEmailCountdown(0);
            setVerifyContext('');
            uploadForm.resetFields();
          }
        }}
        footer={null}
        width={1100}
        style={{ top: '20%' }}
        maskClosable={!uploadingPassport && !confirming}
        keyboard={!uploadingPassport && !confirming}
        destroyOnClose
        afterOpenChange={(open) => {
          if (open && uploadingTourist?.phone) {
            uploadForm.setFieldsValue({ phone: uploadingTourist.phone });
          }
        }}
      >
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          <Step title={selectedTourInfo?.tourType === 'daytour' ? '上传证件' : '上传护照'} />
          <Step title="确认信息" />
        </Steps>

        {/* 步骤1：上传护照/证件 */}
        {currentStep === 0 && (
          <div>
            {uploadingPassport && (
              <div style={{ marginBottom: 16 }}>
                <Progress percent={uploadProgress} status="active" />
                <div style={{ textAlign: 'center', color: '#666', marginTop: 8 }}>{progressText}</div>
              </div>
            )}
            {!uploadingPassport && (
              <>
                {/* 日游产品：支持的证件类型提示 */}
                {selectedTourInfo?.tourType === 'daytour' && (
                  <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, fontSize: 13, color: '#555' }}>
                    支持识别：护照 / 新西兰驾照 / SuperGold Card
                  </div>
                )}
                <Dragger
                  name={selectedTourInfo?.tourType === 'daytour' ? 'document' : 'passport'}
                  multiple={false}
                  showUploadList={false}
                  accept="image/*"
                  beforeUpload={handleManualPassportUpload}
                  disabled={uploadingPassport}
                >
                  <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                  <p className="ant-upload-text">
                    点击或拖拽{selectedTourInfo?.tourType === 'daytour' ? '证件' : '护照'}图片到此区域
                  </p>
                  <p className="ant-upload-hint">
                    {selectedTourInfo?.tourType === 'daytour'
                      ? '支持护照、新西兰驾照、新西兰老年卡（SuperGold Card）'
                      : '支持 JPG/PNG 格式，自动识别护照信息'}
                  </p>
                </Dragger>

                {/* 护照拍照示意图（仅护照场景显示） */}
                {selectedTourInfo?.tourType !== 'daytour' && (
                  <div style={{ marginTop: 16, padding: '12px 14px', background: '#fafcff', border: '1px solid #e6f0ff', borderRadius: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1677ff', marginBottom: 10 }}>
                      📷 护照拍照要求：信息页<strong>所有字段 + 底部两行机读码（MRZ）必须完整拍入</strong>，不被裁切或遮挡
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                      {[
                        { v: 'ok', ok: true, label: '合格', desc: '信息+MRZ完整·清晰' },
                        { v: 'corner', label: '被裁切', desc: '信息或机读码被切掉' },
                        { v: 'block', label: '被遮挡', desc: '手指/物体挡住信息' },
                        { v: 'tilt', label: '模糊反光', desc: '反光或太糊读不清' },
                      ].map((s) => (
                        <div key={s.v} style={{ textAlign: 'center', flex: '1 1 76px' }}>
                          <svg width="76" height="96" viewBox="0 0 76 96">
                            <rect x="2" y="2" width="72" height="92" rx="5" fill="none" stroke={s.ok ? '#52c41a' : '#ff4d4f'} strokeWidth="2" strokeDasharray="5 3" />
                            <g transform={s.v === 'corner' ? 'translate(15 17)' : s.v === 'tilt' ? 'rotate(-14 38 48)' : ''}>
                              <rect x="9" y="13" width="58" height="70" rx="3" fill="#fff" stroke="#1677ff" strokeWidth="1.5" />
                              <rect x="14" y="19" width="15" height="19" rx="1" fill="#cbd5e1" />
                              <line x1="34" y1="21" x2="61" y2="21" stroke="#cbd5e1" strokeWidth="2.5" />
                              <line x1="34" y1="27" x2="57" y2="27" stroke="#e2e8f0" strokeWidth="2.5" />
                              <line x1="34" y1="33" x2="60" y2="33" stroke="#e2e8f0" strokeWidth="2.5" />
                              <line x1="14" y1="47" x2="61" y2="47" stroke="#e2e8f0" strokeWidth="2.5" />
                              <rect x="11" y="66" width="52" height="15" rx="2" fill="#e6f0ff" stroke="#1677ff" strokeWidth="0.8" />
                              <line x1="14" y1="70" x2="61" y2="70" stroke="#1677ff" strokeWidth="2" strokeDasharray="3 1.5" />
                              <line x1="14" y1="76" x2="61" y2="76" stroke="#1677ff" strokeWidth="2" strokeDasharray="3 1.5" />
                            </g>
                            {s.v === 'block' && <ellipse cx="52" cy="34" rx="9" ry="28" fill="#f0b27a" stroke="#d68a52" strokeWidth="1" transform="rotate(22 52 34)" />}
                            <circle cx="63" cy="13" r="9" fill={s.ok ? '#52c41a' : '#ff4d4f'} />
                            <text x="63" y="17" textAnchor="middle" fontSize="11" fill="#fff" fontWeight="bold">{s.ok ? '✓' : '✗'}</text>
                          </svg>
                          <div style={{ fontSize: 12, fontWeight: 600, color: s.ok ? '#52c41a' : '#ff4d4f', marginTop: 2 }}>{s.ok ? '✓ ' : '✗ '}{s.label}</div>
                          <div style={{ fontSize: 11, color: '#999', lineHeight: 1.3 }}>{s.desc}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #d6e4ff', fontSize: 12, color: '#fa8c16', lineHeight: 1.6 }}>
                      ⚠️ 特别注意：护照<strong>底部两行机读码</strong>（上图<span style={{ color: '#1677ff' }}>蓝框 MRZ 区</span>）是识别关键，最容易被裁切，务必完整拍入画面；只要信息和 MRZ 都拍全可读，轻微倾斜不影响，否则会提示“机读区未完整拍摄”需重拍。
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 步骤2：确认信息 - 始终挂载以确保Form.Item注册，通过display控制显隐 */}
        {(() => {
          const isDaytour = selectedTourInfo?.tourType === 'daytour';
          const docType = isDaytour ? uploadDocumentType : 'passport';
          const hideNationality = isDaytour && docType !== 'passport';
          const hideExpiryDate = isDaytour && (docType === 'nz_driver_license' || docType === 'nz_senior_card');
          const hideBirthDate = isDaytour && docType === 'nz_senior_card';
          const hideIssueDate = isDaytour && docType !== 'passport';
          const hideBirthPlace = isDaytour && docType !== 'passport';

          return (
            <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
              <Alert message="请仔细核对以下识别信息是否正确，确认后点击提交" type="info" showIcon style={{ marginBottom: 16 }} />
              <Row gutter={24}>
                <Col xs={24} md={10}>
                  <div style={{ background: '#f8f9fa', padding: 20, borderRadius: 8 }}>
                    <h3>{isDaytour ? '证件照片' : '护照照片'}</h3>
                    {tempImageUrl && (
                      <Image src={tempImageUrl} alt="证件预览" style={{ width: '100%', maxHeight: 400, objectFit: 'contain' }} />
                    )}
                  </div>
                </Col>
                <Col xs={24} md={14}>
                  <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #e8e8e8' }}>
                    <h3>请确认或修改{isDaytour ? '证件' : '护照'}信息</h3>
                    <Form form={uploadForm} layout="vertical">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="fullName" label="姓名（英文）"
                            extra={ocrNameMismatch ? <span style={{ color: '#ff4d4f' }}>{ocrNameMismatch}</span> : getNameFormatHint()}
                            rules={[{ required: true, message: '请输入姓名' }]}
                          >
                            <Input placeholder="姓/名 (例: ZHANG/SAN)" onChange={(e) => uploadForm.setFieldsValue({ fullName: e.target.value.toUpperCase() })} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name={isDaytour ? 'documentNumber' : 'passportNumber'}
                            label={isDaytour ? '证件号码' : '护照号码'}
                            rules={[{ required: true, message: isDaytour ? '请输入证件号码' : '请输入护照号码' }]}
                          >
                            <Input
                              style={{ textTransform: 'uppercase' }}
                              onChange={(e) => {
                                uploadForm.setFieldsValue({ [isDaytour ? 'documentNumber' : 'passportNumber']: e.target.value.toUpperCase() });
                                setTimeout(recheckNZPassport, 0);
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {(!isDaytour || !hideNationality) && (
                        <Row gutter={16}>
                          {!isDaytour && (
                            <Col span={12}>
                              <Form.Item name="gender" label="性别">
                                <Select>
                                  <Option value="M">男/Male</Option>
                                  <Option value="F">女/Female</Option>
                                </Select>
                              </Form.Item>
                            </Col>
                          )}
                          {!hideNationality && (
                            <Col span={12}>
                              <Form.Item name="nationality" label="国籍">
                                <Select showSearch optionFilterProp="label" allowClear onChange={() => setTimeout(recheckNZPassport, 0)}>
                                  {getAllCountries().map(c => (
                                    <Option key={c.value} value={c.value} label={c.label}>{c.label}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            </Col>
                          )}
                        </Row>
                      )}

                      {(!hideBirthDate || !hideExpiryDate) && (
                        <Row gutter={16}>
                          {!hideBirthDate && (
                            <Col span={12}>
                              <Form.Item
                                name="birthDate"
                                label="出生日期"
                                rules={[{ required: !isDaytour, message: '请输入出生日期（OCR 未识别到时必须手动填写）' }, { validator: validateOcrDate }]}
                              >
                                <Input placeholder="DD/MM/YYYY" onChange={() => setTimeout(recheckPassportValidity, 0)} />
                              </Form.Item>
                            </Col>
                          )}
                          {!hideExpiryDate && (
                            <Col span={12}>
                              <Form.Item
                                name="expiryDate"
                                label="有效期至"
                                rules={[{ required: !isDaytour, message: '请输入护照有效期（OCR 未识别到时必须手动填写）' }, { validator: validateOcrDate }]}
                              >
                                <Input placeholder="DD/MM/YYYY" onChange={(e) => { checkPassportExpiry(e.target.value); setTimeout(recheckPassportValidity, 0); }} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>
                      )}

                      {passportExpiryWarning && !hideExpiryDate && (
                        <Alert
                          message={`${isDaytour ? '证件' : '护照'}有效期不足6个月`}
                          description={`有效期（${passportExpiryWarning.expiryDate}）距出团日期（${passportExpiryWarning.departureDate}）仅剩 ${passportExpiryWarning.monthsDiff} 个月`}
                          type="warning" showIcon style={{ marginBottom: 16 }}
                        />
                      )}

                      {passportValidityWarning && (
                        <Alert
                          message="护照有效期与年龄不匹配"
                          description={passportValidityWarning}
                          type="warning" showIcon style={{ marginBottom: 16 }}
                        />
                      )}

                      {nzPassportWarning && (
                        <Alert
                          message="新西兰护照号码格式异常"
                          description={nzPassportWarning}
                          type="warning" showIcon style={{ marginBottom: 16 }}
                        />
                      )}

                      {discountAlert && (
                        discountAlert.eligible ? (
                          <Alert
                            message="符合优惠价条件"
                            description={`出团时年龄 ${discountAlert.age} 岁，满足55岁以上优惠价条件。`}
                            type="success" showIcon style={{ marginBottom: 16 }}
                          />
                        ) : (
                          <Alert
                            message="不满足优惠价格条件，需要补款"
                            description={`出团时年龄 ${discountAlert.age} 岁，不满55岁，无法享受优惠价。`}
                            type="warning" showIcon style={{ marginBottom: 16 }}
                          />
                        )
                      )}

                      {!hideIssueDate && (
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item name="issueDate" label="签发日期">
                              <Input placeholder="DD/MM/YYYY" onChange={() => setTimeout(recheckPassportValidity, 0)} />
                            </Form.Item>
                          </Col>
                          {!hideBirthPlace && (
                            <Col span={12}>
                              <Form.Item name="birthPlace" label="出生地">
                                <Input placeholder="出生地" style={{ textTransform: 'uppercase' }} onChange={(e) => uploadForm.setFieldsValue({ birthPlace: e.target.value.toUpperCase() })} />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>
                      )}

                      {/* 一日游额外字段：电话、邮箱验证 */}
                      {isDaytour && (
                        <>
                          <Form.Item name="phone" label="联系电话（主要联系人电话）" rules={[{ required: true, message: '请输入联系电话' }]}>
                            <Input placeholder="请输入联系电话" />
                          </Form.Item>

                          <Row gutter={16}>
                            <Col span={16}>
                              <Form.Item name="contactEmail" label="联系邮箱" rules={[{ type: 'email', message: '邮箱格式不正确' }]}>
                                <Input
                                  placeholder="请输入联系邮箱"
                                  onChange={() => { if (emailVerified) { setEmailVerified(false); setVerifyContext(''); } }}
                                  suffix={emailVerified ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null}
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.contactEmail !== cur.contactEmail}>
                            {({ getFieldValue }) => {
                              const email = getFieldValue('contactEmail');
                              if (!email || emailVerified) return null;
                              return (
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Form.Item name="verificationCode" label="邮箱验证码">
                                      <Input placeholder="请输入6位验证码" maxLength={6} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item label=" ">
                                      <Space>
                                        <Button onClick={handleSendVerificationCode} loading={sendingCode} disabled={emailCountdown > 0}>
                                          {emailCountdown > 0 ? `${emailCountdown}s后重发` : '发送验证码'}
                                        </Button>
                                        <Button type="primary" onClick={handleVerifyCode} loading={verifyingCode}>验证</Button>
                                      </Space>
                                    </Form.Item>
                                  </Col>
                                </Row>
                              );
                            }}
                          </Form.Item>
                        </>
                      )}
                    </Form>

                    <Space>
                      <Button onClick={handleResetUpload}>重新上传</Button>
                      <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleFinalSubmit} loading={confirming}>
                        确认提交
                      </Button>
                    </Space>
                  </div>
                </Col>
              </Row>
            </div>
          );
        })()}
      </Modal>

      {/* 确认提交弹窗 - 逐项核对 */}
      <Modal
        title="提交前请逐项核对"
        open={confirmModalVisible}
        closable={false}
        maskClosable={false}
        width={880}
        zIndex={1100}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setConfirmModalVisible(false);
              setCountdown(5);
            }}
          >
            返回修改
          </Button>,
          <Button
            key="submit"
            type="primary"
            disabled={countdown > 0 || !confirmChecks.name || !confirmChecks.docNo || !confirmChecks.birthDate || !confirmChecks.expiryDate}
            loading={confirming}
            onClick={handleConfirmAndSubmit}
          >
            {countdown > 0 ? `确定 (${countdown}秒)` : !confirmChecks.name || !confirmChecks.docNo || !confirmChecks.birthDate || !confirmChecks.expiryDate ? '请先勾选全部确认' : '确定提交'}
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
        {/* 左栏：护照照片，对照复查 */}
        <div style={{ width: '380px', flexShrink: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a', marginBottom: '8px' }}>护照照片（对照复查）</div>
          {(() => {
            const imgSrc = tempImageUrl || (uploadingTourist?.passportPhoto ? `${API_BASE.replace('/api', '')}${uploadingTourist.passportPhoto}` : null);
            return imgSrc ? (
              <Image src={imgSrc} alt="护照照片" style={{ width: '100%', borderRadius: '8px', border: '1px solid #f0f0f0' }} />
            ) : (
              <div style={{ width: '100%', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', background: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>暂无照片</div>
            );
          })()}
        </div>
        {/* 右栏：逐项核对 */}
        <div style={{ flex: 1, minWidth: 0 }}>
        <Alert
          message="上传后不再复核"
          description="系统将直接按本次提交的信息执行后续操作，请务必核对无误。"
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        {(() => {
          const fv = uploadForm.getFieldsValue();
          const isDt = selectedTourInfo?.tourType === 'daytour';
          const checkStyle = { display: 'flex', alignItems: 'flex-start', padding: '12px 16px', marginBottom: '8px', background: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' };
          const valStyle = { fontSize: '18px', fontWeight: 700, color: '#1a1a1a', letterSpacing: '0.5px' };
          const labelStyle = { fontSize: '12px', color: '#999', marginBottom: '2px' };
          return (
            <div>
              <div style={checkStyle}>
                <Checkbox
                  checked={confirmChecks.name}
                  onChange={(e) => setConfirmChecks(prev => ({ ...prev, name: e.target.checked }))}
                  style={{ marginRight: '12px', marginTop: '4px' }}
                />
                <div>
                  <div style={labelStyle}>姓名 (Name)</div>
                  <div style={valStyle}>{fv.fullName || '-'}</div>
                </div>
              </div>
              <div style={checkStyle}>
                <Checkbox
                  checked={confirmChecks.docNo}
                  onChange={(e) => setConfirmChecks(prev => ({ ...prev, docNo: e.target.checked }))}
                  style={{ marginRight: '12px', marginTop: '4px' }}
                />
                <div>
                  <div style={labelStyle}>{isDt ? '证件号 (Document No.)' : '护照号 (Passport No.)'}</div>
                  <div style={valStyle}>{(isDt ? fv.documentNumber : fv.passportNumber) || '-'}</div>
                </div>
              </div>
              <div style={checkStyle}>
                <Checkbox
                  checked={confirmChecks.birthDate}
                  onChange={(e) => setConfirmChecks(prev => ({ ...prev, birthDate: e.target.checked }))}
                  style={{ marginRight: '12px', marginTop: '4px' }}
                />
                <div>
                  <div style={labelStyle}>出生日期 (Date of Birth)</div>
                  <div style={valStyle}>{fv.birthDate || '-'}</div>
                </div>
              </div>
              <div style={checkStyle}>
                <Checkbox
                  checked={confirmChecks.expiryDate}
                  onChange={(e) => setConfirmChecks(prev => ({ ...prev, expiryDate: e.target.checked }))}
                  style={{ marginRight: '12px', marginTop: '4px' }}
                />
                <div>
                  <div style={labelStyle}>有效期至 (Expiry Date)</div>
                  <div style={valStyle}>{fv.expiryDate || '-'}</div>
                </div>
              </div>
              <div style={{ textAlign: 'center', color: '#ff4d4f', fontSize: '13px', marginTop: '12px' }}>
                请逐项核对以上信息，勾选确认无误后方可提交
              </div>
            </div>
          );
        })()}
        {countdown > 0 && (
          <div style={{ textAlign: 'center', color: '#1890ff', fontSize: '14px', marginTop: '8px' }}>
            {countdown} 秒后可操作
          </div>
        )}
        </div>
        </div>
      </Modal>

      {/* 上传链接弹窗（一日游） */}
      <Modal
        title="证件上传链接"
        open={uploadLinkModalVisible}
        onCancel={() => setUploadLinkModalVisible(false)}
        style={{ top: '20%' }}
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={async () => {
            try {
              await navigator.clipboard.writeText(newTouristUploadUrl);
              message.success('链接已复制');
            } catch { message.error('复制失败'); }
          }}>复制链接</Button>,
          <Button key="close" onClick={() => setUploadLinkModalVisible(false)}>关闭</Button>
        ]}
      >
        <Alert
          message="游客添加成功！请将以下链接发送给游客，用于上传证件照片。"
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Input value={newTouristUploadUrl} readOnly style={{ marginBottom: 8 }} />
      </Modal>

      {/* 证件图片预览 */}
      <Modal
        title="核对证件"
        open={previewDocVisible}
        onCancel={() => setPreviewDocVisible(false)}
        footer={<Button onClick={() => setPreviewDocVisible(false)}>关闭</Button>}
        width={600}
      >
        {previewDocUrl && <Image src={previewDocUrl} style={{ width: '100%' }} preview={false} />}
      </Modal>

      <VoucherRedeemModal
        open={redeemModalVisible}
        tourist={redeemingTourist}
        onClose={() => { setRedeemModalVisible(false); setRedeemingTourist(null); }}
        onSuccess={() => {
          if (selectedTourInfo?.id) handleViewTourists(selectedTourInfo);
        }}
      />
    </div>
  );
}

export default SalesTours;

